import {
  generateNumberedDisplay,
  normalizeNumberingConfig,
  numberingConfigFromStyle,
  type NumberedItemOrder,
  type NumberingCasing,
  type NumberingConfig,
  type NumberingLevelFormat
} from '@open-dataroom/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouterState,
  useSearch
} from '@tanstack/react-router';
import {
  ChevronRight,
  DoorOpen,
  Download,
  Eye,
  FileText,
  FolderOpen,
  FolderPlus,
  Grid3X3,
  GripVertical,
  Hash,
  Image as ImageIcon,
  Info,
  List,
  Pencil,
  Minus,
  MoreHorizontal,
  Move,
  Plus,
  Share,
  Trash2,
  Upload,
  Users,
  X
} from 'lucide-react';
import * as React from 'react';

import { CreateGroupModal } from '~/components/create-group-modal';
import { InviteRoomViewerModal } from '~/components/invite-room-viewer-modal';
import {
  ShareAccessDialog,
  type ShareAccessTarget
} from '~/components/share-access-dialog';
import { Button } from '~/components/ui/button';
import { Dialog, DialogPanel } from '~/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Separator } from '~/components/ui/separator';
import { Skeleton } from '~/components/ui/skeleton';
import { Toast, type ToastVariant } from '~/components/ui/toast';
import { useAppSettings } from '~/hooks/use-app-settings';
import {
  formatDocumentTitle,
  useDocumentTitle
} from '~/hooks/use-document-title';
import { useRegisterActionBarActions } from '~/lib/action-bar';
import { api } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import {
  canUseDirectoryPicker,
  collectFilesFromDataTransfer,
  hasFolderStructure,
  pickDirectoryWithFsApi,
  type FolderUploadFile
} from '~/lib/collect-folder-files';
import { requireSession } from '~/lib/route-auth';
import { cn } from '~/lib/utils';

export const Route = createFileRoute('/rooms/$roomId')({
  beforeLoad: async () => {
    await requireSession();
  },
  component: RoomExplorerRoute
});

type FolderRow = {
  id: string;
  kind?: 'folder';
  roomId: string;
  parentId: string | null;
  name: string;
  displayNumber: string;
  numberingConfig?: NumberingConfig | null;
  sortOrder: number;
  createdAt?: string | number;
  directFileCount?: number;
};

type FileRow = {
  id: string;
  kind: 'file';
  roomId?: string;
  parentId?: string | null;
  folderId?: string | null;
  name: string;
  displayNumber: string;
  sortOrder: number;
  mimeType: string;
  sizeBytes: number;
  currentVersion: number;
  createdAt: string;
  updatedAt: string;
};

type ItemRow = (FolderRow & { kind: 'folder' }) | (FileRow & { kind: 'file' });

type ArchiveJobResponse = {
  id: string;
  roomId: string;
  requestedBy: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired';
  fileCount: number;
  processedFileCount: number;
  totalBytes: number;
  processedBytes: number;
  filename: string;
  error?: string | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  expiresAt?: string | null;
};

type RenumberDialogItem = NumberedItemOrder & {
  key: string;
  label: string;
  detail: string;
};

type RenumberFormat = Exclude<NumberingLevelFormat, 'digit'>;

// v2 key to reset previously persisted extremes to the new middle default.
const THUMB_STEP_KEY = 'dataroom-thumb-step-v2';
const THUMB_STEPS = 5;
const LIST_THUMB_PX = 44;
const GRID_THUMB_PX = [72, 96, 120, 144, 172] as const;
const DEFAULT_THUMB_STEP = Math.ceil(THUMB_STEPS / 2);
const EXPLORER_LIST_ROW_CLASS =
  'group cursor-pointer select-none outline-none transition-colors hover:[&>td]:bg-accent focus-visible:[&>td]:border-ring focus-visible:[&>td]:bg-accent/80';
const EXPLORER_LIST_ROW_SELECTED_CLASS =
  '[&>td]:border-primary/60 [&>td]:bg-primary/5 hover:[&>td]:bg-primary/10 focus-visible:[&>td]:bg-primary/10';
const EXPLORER_LIST_ROW_IDLE_CLASS = '[&>td]:border-b-border/50';
const EXPLORER_GRID_ITEM_CLASS =
  'group flex w-full cursor-pointer select-none flex-col rounded-md border border-border bg-card p-3 text-left outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const EXPLORER_GRID_ITEM_SELECTED_CLASS =
  'border-primary/60 bg-primary/5 hover:bg-primary/10';
const EXPLORER_LIST_FIRST_CELL_CLASS =
  'rounded-l-md border-y border-l border-transparent py-2.5 pl-4 pr-3 align-middle';
const EXPLORER_LIST_NAME_CELL_CLASS =
  'max-w-0 truncate rounded-r-md border-y border-r border-transparent py-2.5 pl-2 pr-3 align-middle font-medium sm:rounded-r-none sm:border-r-0 sm:pr-0';
const EXPLORER_LIST_MIDDLE_CELL_CLASS =
  'hidden border-y border-transparent py-2.5 align-middle text-sm text-muted-foreground sm:table-cell';
const EXPLORER_LIST_LAST_CELL_CLASS =
  'hidden rounded-r-md border-y border-r border-transparent px-2 py-2.5 align-middle sm:table-cell';

function explorerListRowClass(selected: boolean) {
  return cn(
    EXPLORER_LIST_ROW_CLASS,
    selected ? EXPLORER_LIST_ROW_SELECTED_CLASS : EXPLORER_LIST_ROW_IDLE_CLASS
  );
}

function explorerGridItemClass(selected: boolean, className?: string) {
  return cn(
    EXPLORER_GRID_ITEM_CLASS,
    selected ? EXPLORER_GRID_ITEM_SELECTED_CLASS : null,
    className
  );
}

function ExplorerListColgroup() {
  return (
    <colgroup>
      <col style={{ width: 'calc(var(--list-thumb) + 2rem)' }} />
      <col />
      <col className="hidden sm:table-column" style={{ width: '11rem' }} />
      <col className="hidden sm:table-column" style={{ width: '6rem' }} />
      <col className="hidden sm:table-column" style={{ width: '3rem' }} />
    </colgroup>
  );
}

function ExplorerListThead() {
  return (
    <thead className="sticky top-0 z-10 border-b border-border bg-background text-xs font-medium text-muted-foreground">
      <tr>
        <th scope="col" className="sr-only py-2 pl-4 align-middle">
          Preview
        </th>
        <th
          scope="col"
          className="py-2 pl-2 text-left align-middle font-medium"
        >
          Name
        </th>
        <th
          scope="col"
          className="hidden py-2 text-left align-middle font-medium sm:table-cell"
        >
          Updated
        </th>
        <th
          scope="col"
          className="hidden py-2 text-right align-middle font-medium sm:table-cell"
        >
          Size
        </th>
        <th
          scope="col"
          className="hidden w-12 px-2 py-2 align-middle sm:table-cell"
        >
          <span className="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
  );
}

function explorerFileKey(id: string) {
  return `file:${id}`;
}
function explorerFolderKey(id: string) {
  return `folder:${id}`;
}

type ExplorerContextTarget =
  | { type: 'folder'; id: string; label: string }
  | { type: 'file'; id: string; label: string };

const EXPLORER_CONTEXT_MENU_WIDTH = 224;

function ExplorerRowActionsButton({
  label,
  selected,
  onOpenMenu
}: {
  label: string;
  selected: boolean;
  onOpenMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      data-explorer-row-actions
      onClick={(e) => {
        e.stopPropagation();
        onOpenMenu(e);
      }}
      className={cn(
        'rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground',
        'opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100',
        selected && 'opacity-100'
      )}
      aria-label={`Actions for ${label}`}
      aria-haspopup="menu"
    >
      <MoreHorizontal className="h-4 w-4" aria-hidden />
    </button>
  );
}

type MarqueeRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

function toggleExplorerKey(prev: Set<string>, key: string): Set<string> {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

function rangeBetweenKeys(keys: string[], a: string, b: string): Set<string> {
  const ia = keys.indexOf(a);
  const ib = keys.indexOf(b);
  if (ia === -1 || ib === -1) return new Set([b]);
  const [from, to] = ia <= ib ? [ia, ib] : [ib, ia];
  return new Set(keys.slice(from, to + 1));
}

function parseExplorerKey(
  key: string
): { type: 'file' | 'folder'; id: string } | null {
  if (key.startsWith('file:')) return { type: 'file', id: key.slice(5) };
  if (key.startsWith('folder:')) return { type: 'folder', id: key.slice(7) };
  return null;
}

function rectsIntersect(a: MarqueeRect, b: DOMRect): boolean {
  return (
    a.left <= b.right &&
    a.left + a.width >= b.left &&
    a.top <= b.bottom &&
    a.top + a.height >= b.top
  );
}

function marqueeRectFromPoints(
  startX: number,
  startY: number,
  endX: number,
  endY: number
): MarqueeRect {
  return {
    left: Math.min(startX, endX),
    top: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY)
  };
}

function shouldStartExplorerMarquee(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (
    target.closest(
      '[data-explorer-key], [data-explorer-no-marquee], [data-explorer-context-menu]'
    )
  ) {
    return false;
  }
  if (target.isContentEditable) return false;
  const interactive = target.closest(
    'input, textarea, select, a, [role="menuitem"]'
  );
  return !interactive;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatUpdatedAt(iso: string | number | undefined): string {
  if (iso === undefined || iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
}

function formatForRenumberSelect(format: NumberingLevelFormat): RenumberFormat {
  return format === 'digit' ? 'numeric' : format;
}

function displayLevel(displayNumber: string, separator: string): number {
  if (!separator) return 0;

  let normalized = displayNumber;
  while (normalized.endsWith(separator)) {
    normalized = normalized.slice(0, -separator.length);
  }
  return normalized ? normalized.split(separator).length : 0;
}

export function displayNumberWithSuffix(
  displayNumber: string,
  separator: string
): string {
  if (!displayNumber || !separator || displayNumber.endsWith(separator))
    return displayNumber;
  return `${displayNumber}${separator}`;
}

function displayNumberPrefix(displayNumber: string, separator: string): string {
  const value = displayNumberWithSuffix(displayNumber, separator);
  return value ? `${value} ` : '';
}

function itemLabelWithDisplayNumber(
  displayNumber: string,
  name: string,
  separator: string
): string {
  return `${displayNumberPrefix(displayNumber, separator)}${name}`;
}

function numberingConfigForContainer(
  roomConfig: NumberingConfig | undefined,
  folder: FolderRow | null | undefined
): NumberingConfig {
  const fallback =
    roomConfig ?? numberingConfigFromStyle({ format: 'numeric' });
  if (folder?.numberingConfig) return folder.numberingConfig;

  const levels = fallback.levels ?? [];
  if (levels.length === 0) return fallback;

  const separator = normalizeNumberingConfig(fallback).separator;
  const level = folder ? displayLevel(folder.displayNumber, separator) : 0;
  const levelConfig = levels[level] ?? levels[levels.length - 1];
  if (!levelConfig) return fallback;
  return numberingConfigFromStyle({
    format: levelConfig.format,
    padding: levelConfig.padding,
    casing: levelConfig.casing,
    separator
  });
}

function thumbnailEdgeForDisplay(sizePx: number): number {
  const dpr =
    typeof window !== 'undefined'
      ? Math.min(window.devicePixelRatio || 1, 2)
      : 1;
  return Math.min(512, Math.max(32, Math.ceil(sizePx * dpr)));
}

function isSvgMimeType(mimeType: string | null | undefined): boolean {
  return mimeType === 'image/svg+xml' || mimeType === 'image/svg';
}

function isRasterImageMimeType(mimeType: string | null | undefined): boolean {
  return (
    !!mimeType && mimeType.startsWith('image/') && !isSvgMimeType(mimeType)
  );
}

function ExplorerItemThumb({
  sizePx,
  folder,
  mimeType,
  fileId
}: {
  sizePx: number;
  folder: boolean;
  mimeType?: string;
  fileId?: string;
}) {
  const iconPx = Math.max(16, Math.round(sizePx * 0.45));
  const isImage = !folder && !!fileId && isRasterImageMimeType(mimeType);
  const thumbSrc = isImage
    ? `/api/files/${fileId}/thumbnail?w=${thumbnailEdgeForDisplay(sizePx)}`
    : null;

  const [imageFailed, setImageFailed] = React.useState(false);
  React.useEffect(() => {
    setImageFailed(false);
  }, [thumbSrc]);

  return (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40"
      style={{ width: sizePx, height: sizePx }}
    >
      {folder ? (
        <FolderOpen
          className="text-muted-foreground"
          style={{ width: iconPx, height: iconPx }}
          aria-hidden
        />
      ) : isImage && thumbSrc && !imageFailed ? (
        <img
          src={thumbSrc}
          alt=""
          width={sizePx}
          height={sizePx}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : isRasterImageMimeType(mimeType) || isSvgMimeType(mimeType) ? (
        <ImageIcon
          className="text-sky-400/90"
          style={{ width: iconPx, height: iconPx }}
          aria-hidden
        />
      ) : mimeType === 'application/pdf' ? (
        <FileText
          className="text-red-400/90"
          style={{ width: iconPx, height: iconPx }}
          aria-hidden
        />
      ) : (
        <FileText
          className="text-muted-foreground"
          style={{ width: iconPx, height: iconPx }}
          aria-hidden
        />
      )}
    </div>
  );
}

function breadcrumbForFolder(
  folderId: string | null,
  roomName: string,
  byId: Map<string, FolderRow>,
  separator: string
): Array<{ id: string | null; label: string }> {
  const root = { id: null as string | null, label: roomName };
  if (!folderId) return [root];
  const chain: Array<{ id: string | null; label: string }> = [];
  let cur: string | null = folderId;
  while (cur) {
    const f = byId.get(cur);
    if (!f) break;
    chain.unshift({
      id: f.id,
      label: itemLabelWithDisplayNumber(f.displayNumber, f.name, separator)
    });
    cur = f.parentId;
  }
  return [root, ...chain];
}

type BreadcrumbItem =
  | { type: 'crumb'; id: string | null; label: string }
  | { type: 'ellipsis' };

function collapseBreadcrumb(
  crumbs: Array<{ id: string | null; label: string }>,
  opts?: { leading?: number; trailing?: number }
): BreadcrumbItem[] {
  const leading = opts?.leading ?? 1;
  const trailing = opts?.trailing ?? 2;

  if (crumbs.length <= leading + trailing + 1) {
    return crumbs.map((c) => ({ type: 'crumb', ...c }));
  }

  const head = crumbs
    .slice(0, leading)
    .map((c) => ({ type: 'crumb', ...c }) satisfies BreadcrumbItem);
  const tail = crumbs
    .slice(Math.max(crumbs.length - trailing, leading))
    .map((c) => ({ type: 'crumb', ...c }) satisfies BreadcrumbItem);

  return [...head, { type: 'ellipsis' }, ...tail];
}

function depthOf(folderId: string, byId: Map<string, FolderRow>): number {
  let d = 0;
  let cur: string | null = folderId;
  while (cur) {
    const f = byId.get(cur);
    if (!f?.parentId) break;
    d++;
    cur = f.parentId;
  }
  return d;
}

function folderAncestorPath(
  folderId: string | null,
  byId: Map<string, FolderRow>
): string[] {
  if (!folderId) return [];
  const path: string[] = [];
  let cur: string | null = folderId;
  while (cur) {
    path.unshift(cur);
    const f = byId.get(cur);
    cur = f?.parentId ?? null;
  }
  return path;
}

export function excludedFolderIdsForMove(
  folders: Array<{ id: string; parentId: string | null }>,
  selectedFolderIds: string[]
): Set<string> {
  const excluded = new Set<string>(selectedFolderIds);
  const childrenByParent = new Map<string | null, string[]>();
  for (const f of folders) {
    const list = childrenByParent.get(f.parentId) ?? [];
    list.push(f.id);
    childrenByParent.set(f.parentId, list);
  }
  function addDescendants(parentId: string) {
    for (const childId of childrenByParent.get(parentId) ?? []) {
      excluded.add(childId);
      addDescendants(childId);
    }
  }
  for (const id of selectedFolderIds) {
    addDescendants(id);
  }
  return excluded;
}

export function initialMovePickerPath(
  browseFolderId: string | null,
  folderById: Map<string, FolderRow>,
  excludedFolderIds: Set<string>
): string[] {
  const path = folderAncestorPath(browseFolderId, folderById);
  while (path.length > 0 && excludedFolderIds.has(path[path.length - 1]!)) {
    path.pop();
  }
  return path;
}

export function hasImmediatelySelectedFiles(
  explorerSelection: Iterable<string>
): boolean {
  for (const key of explorerSelection) {
    if (key.startsWith('file:')) return true;
  }
  return false;
}

export function isMoveDestinationRoot(movePath: string[]): boolean {
  return movePath.length === 0;
}

/** Files selected directly (not only inside selected folders) cannot move to room root. */
export function canMoveSelectionToDestination(
  explorerSelection: Iterable<string>,
  movePath: string[]
): boolean {
  if (!hasImmediatelySelectedFiles(explorerSelection)) return true;
  return !isMoveDestinationRoot(movePath);
}

export function shouldUseBulkArchiveDownload(selection: {
  count: number;
  folderIds: string[];
}): boolean {
  if (selection.count === 0) return false;
  if (selection.count === 1 && selection.folderIds.length === 0) return false;
  return true;
}

export function RoomExplorerComponent({ roomId }: { roomId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/rooms/$roomId' }) as { folderId?: string };
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [browseFolderId, setBrowseFolderId] = React.useState<string | null>(
    search.folderId ?? null
  );
  const [selectedFileId, setSelectedFileId] = React.useState<string | null>(
    null
  );
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [createFolderOpen, setCreateFolderOpen] = React.useState(false);
  const [createGroupOpen, setCreateGroupOpen] = React.useState(false);
  const [inviteRoomViewerOpen, setInviteRoomViewerOpen] = React.useState(false);
  const [sharePermissionsTarget, setSharePermissionsTarget] =
    React.useState<ShareAccessTarget | null>(null);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameTarget, setRenameTarget] =
    React.useState<ExplorerContextTarget | null>(null);
  const [renameValue, setRenameValue] = React.useState('');
  const renameInputRef = React.useRef<HTMLInputElement>(null);
  const [renumberOpen, setRenumberOpen] = React.useState(false);
  const [renumberItems, setRenumberItems] = React.useState<
    RenumberDialogItem[]
  >([]);
  const [renumberFormat, setRenumberFormat] =
    React.useState<RenumberFormat>('numeric');
  const [renumberCasing, setRenumberCasing] =
    React.useState<NumberingCasing>('upper');
  const [renumberPadding, setRenumberPadding] = React.useState(2);
  const [renumberDragKey, setRenumberDragKey] = React.useState<string | null>(
    null
  );
  const [explorerSelection, setExplorerSelection] = React.useState<Set<string>>(
    () => new Set()
  );
  const [explorerSelectionAnchor, setExplorerSelectionAnchor] = React.useState<
    string | null
  >(null);
  const [explorerMarqueeRect, setExplorerMarqueeRect] =
    React.useState<MarqueeRect | null>(null);
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  const [dropActive, setDropActive] = React.useState(false);
  const [uploadBusy, setUploadBusy] = React.useState(false);
  const [uploadToast, setUploadToast] = React.useState<{
    open: boolean;
    title: string;
    description?: string;
    variant?: ToastVariant;
    durationMs?: number;
  }>({ open: false, title: '' });
  const [archiveJob, setArchiveJob] = React.useState<ArchiveJobResponse | null>(
    null
  );
  const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);
  const [archiveBusy, setArchiveBusy] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteConfirmTargets, setDeleteConfirmTargets] =
    React.useState<DeleteTargets | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = React.useState(false);
  const [movePath, setMovePath] = React.useState<string[]>([]);
  const [contextMenu, setContextMenu] = React.useState<{
    open: boolean;
    x: number;
    y: number;
    target: ExplorerContextTarget | null;
  }>({ open: false, x: 0, y: 0, target: null });
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const folderInputRef = React.useRef<HTMLInputElement>(null);
  const [folderPickerOpen, setFolderPickerOpen] = React.useState(false);
  const [folderPickerFiles, setFolderPickerFiles] = React.useState<
    FolderUploadFile[]
  >([]);
  const explorerViewportRef = React.useRef<HTMLDivElement>(null);
  const explorerMarqueeStartRef = React.useRef<{
    x: number;
    y: number;
    additive: boolean;
    initialSelection: Set<string>;
  } | null>(null);
  const explorerMarqueePointerIdRef = React.useRef<number | null>(null);
  const explorerMarqueeDidDragRef = React.useRef(false);
  const explorerMarqueeSelectionRef = React.useRef<Set<string>>(new Set());
  const archiveEventsRef = React.useRef<EventSource | null>(null);
  const archiveReconnectAttemptsRef = React.useRef(0);
  const suppressExplorerClickRef = React.useRef(false);
  const newFolderTitleId = React.useId();

  React.useEffect(() => {
    const next = search.folderId ?? null;
    setBrowseFolderId((prev) => (prev === next ? prev : next));
  }, [search.folderId]);

  const setBrowseFolderIdAndUrl = React.useCallback(
    (folderId: string | null, opts?: { replace?: boolean }) => {
      setBrowseFolderId(folderId);
      void navigate({
        to: '/rooms/$roomId',
        params: { roomId },
        search: (prev) => ({ ...prev, folderId: folderId ?? undefined }),
        replace: opts?.replace ?? true
      });
    },
    [navigate, roomId]
  );

  const openFilePreview = React.useCallback(
    (file: { id: string }) => {
      void navigate({
        to: '/rooms/$roomId/files/$fileId',
        params: { roomId, fileId: file.id },
        search: { folderId: browseFolderId ?? undefined }
      });
    },
    [browseFolderId, navigate, roomId]
  );

  const isPreviewableMimeType = (mimeType: string | null | undefined) =>
    mimeType === 'application/pdf' || isRasterImageMimeType(mimeType);

  const [viewMode, setViewMode] = React.useState<'list' | 'grid'>(() => {
    try {
      return (
        (localStorage.getItem('dataroom-view-mode') as 'list' | 'grid') ||
        'list'
      );
    } catch {
      return 'list';
    }
  });

  const [gridThumbStep, setGridThumbStep] = React.useState<number>(() => {
    try {
      const raw = localStorage.getItem(THUMB_STEP_KEY);
      const n = raw ? Number.parseInt(raw, 10) : DEFAULT_THUMB_STEP;
      return n >= 1 && n <= THUMB_STEPS ? n : DEFAULT_THUMB_STEP;
    } catch {
      return DEFAULT_THUMB_STEP;
    }
  });

  const listThumbPx = LIST_THUMB_PX;
  const gridThumbPx = GRID_THUMB_PX[gridThumbStep - 1]!;

  const bumpThumbStep = (delta: number) => {
    setGridThumbStep((prev) => {
      const next = Math.min(THUMB_STEPS, Math.max(1, prev + delta));
      try {
        localStorage.setItem(THUMB_STEP_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const { data: roomData } = useQuery({
    queryKey: ['room', roomId],
    queryFn: () =>
      api.get<{
        room: {
          id: string;
          name: string;
          numberingConfig: NumberingConfig;
        };
      }>(`/api/rooms/${roomId}`)
  });

  const { data: foldersData } = useQuery({
    queryKey: ['folders', roomId],
    queryFn: () =>
      api.get<{ folders: FolderRow[] }>(`/api/folders?roomId=${roomId}`)
  });

  const folders = foldersData?.folders ?? [];
  const folderById = React.useMemo(
    () => new Map(folders.map((f) => [f.id, f])),
    [folders]
  );
  const folderIdByParentAndName = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const f of folders) {
      map.set(`${f.parentId ?? 'root'}:${f.name}`, f.id);
    }
    return map;
  }, [folders]);

  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['items', roomId, browseFolderId],
    queryFn: () => {
      const params = new URLSearchParams({ roomId, limit: '100' });
      if (browseFolderId) params.set('parentId', browseFolderId);
      return api.get<{
        items: ItemRow[];
        total: number;
        nextCursor: string | null;
      }>(`/api/items?${params.toString()}`);
    }
  });

  const room = roomData?.room;
  const currentItems = itemsData?.items ?? [];
  const childFolders = React.useMemo(
    () =>
      currentItems.filter(
        (item): item is FolderRow & { kind: 'folder' } => item.kind === 'folder'
      ),
    [currentItems]
  );
  const files = React.useMemo(
    () =>
      currentItems.filter(
        (item): item is FileRow & { kind: 'file' } => item.kind === 'file'
      ),
    [currentItems]
  );
  const gridItems = React.useMemo(
    () => [...childFolders, ...files],
    [childFolders, files]
  );
  const currentFolder = browseFolderId ? folderById.get(browseFolderId) : null;
  const activeNumberingConfig = numberingConfigForContainer(
    room?.numberingConfig,
    currentFolder
  );
  const activeNumberingStyle = normalizeNumberingConfig(activeNumberingConfig);
  const activeNumberingSeparator = activeNumberingStyle.separator;
  const parentDisplayNumber = currentFolder?.displayNumber ?? null;
  const orderedRenumberSource = React.useMemo<RenumberDialogItem[]>(() => {
    const folderItems = childFolders.map((folder) => ({
      type: 'folder' as const,
      id: folder.id,
      key: explorerFolderKey(folder.id),
      label: folder.name,
      detail: `${displayNumberPrefix(folder.displayNumber, activeNumberingSeparator)}folder`,
      sortOrder: folder.sortOrder
    }));
    const fileItems =
      browseFolderId === null
        ? []
        : files.map((file) => ({
            type: 'file' as const,
            id: file.id,
            key: explorerFileKey(file.id),
            label: file.name,
            detail: `${displayNumberPrefix(file.displayNumber, activeNumberingSeparator)}file`,
            sortOrder: file.sortOrder
          }));
    return [...folderItems, ...fileItems]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(({ sortOrder: _sortOrder, ...item }) => item);
  }, [activeNumberingSeparator, browseFolderId, childFolders, files]);
  const selectedFile = files.find((f) => f.id === selectedFileId);
  const breadcrumb = breadcrumbForFolder(
    browseFolderId,
    room?.name ?? 'Room',
    folderById,
    activeNumberingSeparator
  );
  const { data: settings } = useAppSettings();
  const companyName = settings?.companyName ?? 'Open Dataroom';
  const explorerPageTitle =
    breadcrumb[breadcrumb.length - 1]?.label ?? room?.name ?? 'Room';
  useDocumentTitle(formatDocumentTitle(explorerPageTitle, companyName));
  const breadcrumbItems = React.useMemo(
    () => collapseBreadcrumb(breadcrumb),
    [breadcrumb]
  );
  const collapsedBreadcrumb = React.useMemo(() => {
    const leading = 1;
    const trailing = 2;
    if (breadcrumb.length <= leading + trailing + 1) return [];
    return breadcrumb.slice(
      leading,
      Math.max(leading, breadcrumb.length - trailing)
    );
  }, [breadcrumb]);
  const deleteTargetCount =
    explorerSelection.size > 0
      ? explorerSelection.size
      : selectedFileId
        ? 1
        : 0;
  const canOpenRenumber =
    isAdmin &&
    deleteTargetCount === 0 &&
    viewMode === 'list' &&
    orderedRenumberSource.length > 0;
  const isNumericRenumberFormat = renumberFormat === 'numeric';

  const selectionInfo = React.useMemo(() => {
    const keys = [...explorerSelection];
    const parsed = keys
      .map(parseExplorerKey)
      .filter((v): v is NonNullable<typeof v> => !!v);
    const fileIds = parsed.filter((p) => p.type === 'file').map((p) => p.id);
    const folderIds = parsed
      .filter((p) => p.type === 'folder')
      .map((p) => p.id);
    const single = parsed.length === 1 ? parsed[0] : null;
    return {
      count: parsed.length,
      fileIds,
      folderIds,
      single
    };
  }, [explorerSelection]);

  const visibleExplorerKeys = React.useMemo(() => {
    const folderKeys = childFolders.map((f) => explorerFolderKey(f.id));
    if (browseFolderId === null) return folderKeys;
    if (viewMode === 'grid') {
      return gridItems.map((item) =>
        item.kind === 'folder'
          ? explorerFolderKey(item.id)
          : explorerFileKey(item.id)
      );
    }
    const fileKeys = files.map((f) => explorerFileKey(f.id));
    return [...folderKeys, ...fileKeys];
  }, [browseFolderId, childFolders, files, gridItems, viewMode]);

  const selectKey = React.useCallback(
    (
      key: string,
      e?: { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean }
    ) => {
      const toggle = !!e?.metaKey || !!e?.ctrlKey;
      const extend = !!e?.shiftKey;

      if (extend && explorerSelectionAnchor) {
        const range = rangeBetweenKeys(
          visibleExplorerKeys,
          explorerSelectionAnchor,
          key
        );
        setExplorerSelection((prev) =>
          toggle ? new Set([...prev, ...range]) : range
        );
        return;
      }

      // Plain click on an already-selected item should unselect it (Finder/Explorer style toggle).
      setExplorerSelection((prev) => {
        if (toggle) return toggleExplorerKey(prev, key);

        if (prev.has(key)) {
          if (prev.size === 1) {
            setExplorerSelectionAnchor(null);
            return new Set();
          }
          const next = new Set(prev);
          next.delete(key);
          setExplorerSelectionAnchor(null);
          return next;
        }

        return new Set([key]);
      });
      if (!toggle) setExplorerSelectionAnchor(key);
    },
    [explorerSelectionAnchor, visibleExplorerKeys]
  );

  React.useEffect(() => {
    setExplorerSelection(new Set());
    setExplorerSelectionAnchor(null);
  }, [browseFolderId]);

  React.useEffect(() => {
    if (selectionInfo.count !== 1 || !selectionInfo.single) {
      setSelectedFileId(null);
      return;
    }
    if (selectionInfo.single.type === 'file') {
      setSelectedFileId(selectionInfo.single.id);
      return;
    }
    setSelectedFileId(null);
  }, [selectionInfo.count, selectionInfo.single]);

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      const body: { roomId: string; name: string; parentId?: string } = {
        roomId,
        name: name.trim()
      };
      if (browseFolderId) body.parentId = browseFolderId;
      return api.post<{ folder: FolderRow }>('/api/folders', body);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['folders', roomId] });
      void queryClient.invalidateQueries({ queryKey: ['items', roomId] });
      setCreateFolderOpen(false);
      setNewFolderName('');
      setBrowseFolderIdAndUrl(data.folder.id);
      setSelectedFileId(null);
    }
  });

  const renameFolderMutation = useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      return api.patch<{ success: true }>(`/api/folders/${input.id}`, {
        name: input.name.trim()
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders', roomId] });
      await queryClient.invalidateQueries({ queryKey: ['items', roomId] });
    }
  });

  const renameFileMutation = useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      return api.patch<{ success: true }>(`/api/files/${input.id}`, {
        name: input.name.trim()
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['items', roomId] });
    }
  });

  const renumberMutation = useMutation({
    mutationFn: async (input: {
      items: NumberedItemOrder[];
      numberingConfig: NumberingConfig;
    }) => {
      const path = browseFolderId
        ? `/api/folders/${browseFolderId}/renumber-list`
        : `/api/rooms/${roomId}/renumber-list`;
      return api.post<{ success: true }>(path, input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['room', roomId] });
      await queryClient.invalidateQueries({ queryKey: ['folders', roomId] });
      await queryClient.invalidateQueries({ queryKey: ['items', roomId] });
    }
  });

  const moveMutation = useMutation({
    mutationFn: async (input: {
      itemIds: string[];
      destinationFolderId: string | null;
    }) => {
      return api.post<{ success: true; moved: number }>(
        '/api/items/move',
        input
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['items', roomId] });
      await queryClient.invalidateQueries({ queryKey: ['folders', roomId] });
      await queryClient.invalidateQueries({ queryKey: ['room', roomId] });
      setExplorerSelection(new Set());
      setExplorerSelectionAnchor(null);
      setSelectedFileId(null);
    }
  });

  const openRenameDialog = React.useCallback(
    (target: ExplorerContextTarget) => {
      if (!isAdmin) return;
      setRenameTarget(target);
      setRenameValue(target.label);
      setRenameOpen(true);
    },
    [isAdmin]
  );

  const openSharePermissionsDialog = React.useCallback(
    (target: ShareAccessTarget) => {
      if (!isAdmin) return;
      setSharePermissionsTarget(target);
    },
    [isAdmin]
  );

  const openRenumberDialog = React.useCallback(() => {
    if (
      !isAdmin ||
      explorerSelection.size > 0 ||
      orderedRenumberSource.length === 0
    )
      return;
    setRenumberItems(orderedRenumberSource);
    setRenumberFormat(formatForRenumberSelect(activeNumberingStyle.format));
    setRenumberCasing(activeNumberingStyle.casing);
    setRenumberPadding(activeNumberingStyle.padding);
    setRenumberOpen(true);
  }, [
    activeNumberingStyle.casing,
    activeNumberingStyle.format,
    activeNumberingStyle.padding,
    explorerSelection.size,
    isAdmin,
    orderedRenumberSource
  ]);

  const moveRenumberItem = React.useCallback(
    (fromKey: string, toKey: string) => {
      if (fromKey === toKey) return;
      setRenumberItems((prev) => {
        const from = prev.findIndex((item) => item.key === fromKey);
        const to = prev.findIndex((item) => item.key === toKey);
        if (from === -1 || to === -1) return prev;
        const next = [...prev];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item!);
        return next;
      });
    },
    []
  );

  const handleViewModeChange = (mode: string) => {
    if (mode === 'list' || mode === 'grid') {
      setViewMode(mode);
      localStorage.setItem('dataroom-view-mode', mode);
    }
  };

  const closeContextMenu = React.useCallback(() => {
    setContextMenu((prev) =>
      prev.open ? { ...prev, open: false, target: null } : prev
    );
  }, []);

  React.useEffect(() => {
    if (!contextMenu.open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t?.closest?.(
          '[data-explorer-context-menu], [data-explorer-row-actions]'
        )
      ) {
        return;
      }
      closeContextMenu();
    };
    const onScroll = () => closeContextMenu();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown, { capture: true });
    window.addEventListener('scroll', onScroll, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown, {
        capture: true
      } as never);
      window.removeEventListener('scroll', onScroll, {
        capture: true
      } as never);
    };
  }, [closeContextMenu, contextMenu.open]);

  React.useEffect(() => {
    if (!renameOpen) return;
    const input = renameInputRef.current;
    if (!input) return;
    const target = renameTarget;
    if (!target) return;
    // Defer to ensure the input has mounted and autoFocus has fired.
    const timer = setTimeout(() => {
      if (target.type === 'file') {
        const lastDot = target.label.lastIndexOf('.');
        if (lastDot > 0) {
          input.setSelectionRange(0, lastDot);
          return;
        }
      }
      input.select();
    }, 0);
    return () => clearTimeout(timer);
  }, [renameOpen, renameTarget]);

  const openExplorerContextMenu = React.useCallback(
    (
      e: React.MouseEvent,
      target: ExplorerContextTarget,
      key: string,
      opts?: {
        setSelectedFile?: boolean;
        openOnFolder?: boolean;
        anchor?: 'pointer' | 'trigger';
      }
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const alreadySelected = explorerSelection.has(key);
      if (!alreadySelected) {
        setExplorerSelection(new Set([key]));
        setExplorerSelectionAnchor(key);
      }

      if (target.type === 'file') {
        setSelectedFileId(target.id);
      } else if (opts?.openOnFolder) {
        setBrowseFolderIdAndUrl(target.id);
        setSelectedFileId(null);
      }

      let x = e.clientX;
      let y = e.clientY;
      if (opts?.anchor === 'trigger') {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        x = rect.right - EXPLORER_CONTEXT_MENU_WIDTH;
        y = rect.bottom + 4;
      }

      setContextMenu({
        open: true,
        x,
        y,
        target
      });
    },
    [explorerSelection]
  );

  type DeleteTargets = { fileIds: string[]; folderIds: string[] };

  const getDeleteTargets = (): DeleteTargets => {
    if (explorerSelection.size > 0) {
      const fileIds = [...explorerSelection]
        .filter((k) => k.startsWith('file:'))
        .map((k) => k.slice(5));
      const folderIds = [...explorerSelection]
        .filter((k) => k.startsWith('folder:'))
        .map((k) => k.slice(7));
      return { fileIds, folderIds };
    }
    if (selectedFileId) return { fileIds: [selectedFileId], folderIds: [] };
    return { fileIds: [], folderIds: [] };
  };

  const selectionStats = React.useMemo(() => {
    const { fileIds, folderIds } = getDeleteTargets();
    return {
      count: fileIds.length + folderIds.length,
      fileIds,
      folderIds
    };
  }, [explorerSelection, selectedFileId]);

  const renameBusy =
    renameFolderMutation.isPending || renameFileMutation.isPending;
  const canRename =
    isAdmin &&
    selectionStats.count === 1 &&
    ((contextMenu.target?.type === 'file' &&
      selectionStats.fileIds.length === 1) ||
      (contextMenu.target?.type === 'folder' &&
        selectionStats.folderIds.length === 1));

  const handleDownload = async (fileId: string) => {
    window.open(
      `/api/files/${fileId}/download?disposition=attachment`,
      '_blank'
    );
  };

  const useBulkArchiveDownload = shouldUseBulkArchiveDownload(selectionInfo);
  const canBulkDownloadSelection = true;
  const bulkDownloadLabel =
    selectionInfo.count > 0
      ? archiveBusy && useBulkArchiveDownload
        ? 'Preparing ZIP…'
        : selectionInfo.count === 1 && !useBulkArchiveDownload
          ? 'Download'
          : 'Download selected'
      : archiveBusy
        ? 'Preparing ZIP…'
        : 'Download folder';

  const closeArchiveEvents = React.useCallback(() => {
    archiveEventsRef.current?.close();
    archiveEventsRef.current = null;
  }, []);

  React.useEffect(() => closeArchiveEvents, [closeArchiveEvents]);

  const handleBulkDownloadSelected = React.useCallback(async () => {
    if (!canBulkDownloadSelection || archiveBusy) return;
    closeContextMenu();
    setArchiveBusy(true);
    setArchiveDialogOpen(true);
    setArchiveJob(null);
    try {
      const created = await api.post<{ job: ArchiveJobResponse }>(
        '/api/archives',
        {
          roomId,
          parentId: browseFolderId,
          fileIds: selectionInfo.fileIds,
          folderIds: selectionInfo.folderIds
        }
      );
      setArchiveJob(created.job);

      const finishArchiveFailure = (message: string) => {
        archiveReconnectAttemptsRef.current = 0;
        setArchiveBusy(false);
        setArchiveJob((current) =>
          current ? { ...current, status: 'failed', error: message } : current
        );
      };

      const handleArchiveStatus = (
        next: ArchiveJobResponse,
        events: EventSource
      ) => {
        archiveReconnectAttemptsRef.current = 0;
        setArchiveJob(next);
        if (next.status === 'completed') {
          events.close();
          archiveEventsRef.current = null;
          archiveReconnectAttemptsRef.current = 0;
          setArchiveBusy(false);
          window.location.href = `/api/archives/${next.id}/download`;
        } else if (next.status === 'failed' || next.status === 'expired') {
          events.close();
          archiveEventsRef.current = null;
          archiveReconnectAttemptsRef.current = 0;
          setArchiveBusy(false);
        }
      };

      const pollArchiveJob = async (
        jobId: string
      ): Promise<ArchiveJobResponse | null> => {
        try {
          const { job } = await api.get<{ job: ArchiveJobResponse }>(
            `/api/archives/${jobId}`
          );
          return job;
        } catch {
          return null;
        }
      };

      const openArchiveEvents = (jobId: string) => {
        closeArchiveEvents();
        const events = new EventSource(`/api/archives/${jobId}/events`, {
          withCredentials: true
        });
        archiveEventsRef.current = events;
        events.addEventListener('status', (event) => {
          const next = JSON.parse(
            (event as MessageEvent<string>).data
          ) as ArchiveJobResponse;
          handleArchiveStatus(next, events);
        });
        events.addEventListener('fatal', (event) => {
          events.close();
          archiveEventsRef.current = null;
          let message = 'Archive failed.';
          try {
            const payload = JSON.parse(
              (event as MessageEvent<string>).data
            ) as { error?: string };
            if (payload.error) message = payload.error;
          } catch {
            /* use default message */
          }
          finishArchiveFailure(message);
        });
        events.addEventListener('error', () => {
          events.close();
          archiveEventsRef.current = null;
          void (async () => {
            const job = await pollArchiveJob(jobId);
            if (!job) {
              finishArchiveFailure('Archive job not found or unavailable.');
              return;
            }
            if (job.status === 'completed') {
              archiveReconnectAttemptsRef.current = 0;
              setArchiveBusy(false);
              window.location.href = `/api/archives/${job.id}/download`;
              return;
            }
            if (job && (job.status === 'failed' || job.status === 'expired')) {
              archiveReconnectAttemptsRef.current = 0;
              setArchiveBusy(false);
              setArchiveJob(job);
              return;
            }
            if (archiveReconnectAttemptsRef.current < 8) {
              archiveReconnectAttemptsRef.current += 1;
              window.setTimeout(
                () => openArchiveEvents(jobId),
                500 * archiveReconnectAttemptsRef.current
              );
              return;
            }
            finishArchiveFailure(
              'Lost connection while waiting for the ZIP. Try again if the download does not start.'
            );
          })();
        });
      };

      archiveReconnectAttemptsRef.current = 0;
      openArchiveEvents(created.job.id);
    } catch (e) {
      setArchiveBusy(false);
      setArchiveJob({
        id: '',
        roomId,
        requestedBy: '',
        status: 'failed',
        fileCount: 0,
        processedFileCount: 0,
        totalBytes: 0,
        processedBytes: 0,
        filename: '',
        error:
          e instanceof Error
            ? e.message
            : 'Could not start archive generation.',
        createdAt: new Date().toISOString()
      });
    }
  }, [
    archiveBusy,
    canBulkDownloadSelection,
    closeContextMenu,
    closeArchiveEvents,
    browseFolderId,
    roomId,
    selectionInfo.fileIds,
    selectionInfo.folderIds
  ]);

  const handleDownloadSelected = React.useCallback(() => {
    if (useBulkArchiveDownload || selectionInfo.count === 0) {
      void handleBulkDownloadSelected();
      return;
    }
    const fileId = selectionInfo.fileIds[0];
    if (fileId) void handleDownload(fileId);
  }, [
    handleBulkDownloadSelected,
    handleDownload,
    selectionInfo.count,
    selectionInfo.fileIds,
    useBulkArchiveDownload
  ]);

  const selectedSingleFolder = React.useMemo(() => {
    if (selectionInfo.count !== 1 || selectionInfo.single?.type !== 'folder')
      return null;
    return folderById.get(selectionInfo.single.id) ?? null;
  }, [folderById, selectionInfo.count, selectionInfo.single]);
  const folderPermissionsMenuTarget =
    React.useMemo<ShareAccessTarget | null>(() => {
      const folder =
        selectionInfo.count === 0 ? currentFolder : selectedSingleFolder;
      if (folder) {
        return {
          type: 'folder',
          id: folder.id,
          label: itemLabelWithDisplayNumber(
            folder.displayNumber,
            folder.name,
            activeNumberingSeparator
          )
        };
      }
      if (selectionInfo.count === 0 && room) {
        return { type: 'room', id: room.id, label: room.name };
      }
      return null;
    }, [
      activeNumberingSeparator,
      currentFolder,
      room,
      selectedSingleFolder,
      selectionInfo.count
    ]);
  const canOpenFolderPermissionsFromMenu =
    isAdmin &&
    !!folderPermissionsMenuTarget &&
    (selectionInfo.count === 0 ||
      (selectionInfo.count === 1 && selectionInfo.single?.type === 'folder'));

  const openDeleteConfirm = (targetsOverride?: DeleteTargets) => {
    const targets = targetsOverride ?? getDeleteTargets();
    const n = targets.fileIds.length + targets.folderIds.length;
    if (n === 0 || !isAdmin || deleteBusy) return;
    setDeleteConfirmTargets(targets);
    setDeleteConfirmOpen(true);
  };

  const openMoveDialog = React.useCallback(() => {
    const selectedFolderIds = [...explorerSelection]
      .filter((k) => k.startsWith('folder:'))
      .map((k) => k.slice(7));
    const excluded = excludedFolderIdsForMove(folders, selectedFolderIds);
    setMovePath(initialMovePickerPath(browseFolderId, folderById, excluded));
    setMoveDialogOpen(true);
  }, [browseFolderId, explorerSelection, folderById, folders]);

  const moveBlocksRootDestination =
    hasImmediatelySelectedFiles(explorerSelection);
  const canConfirmMove = canMoveSelectionToDestination(
    explorerSelection,
    movePath
  );

  const executeDeleteConfirmed = async () => {
    if (!deleteConfirmTargets) return;
    const { fileIds, folderIds } = deleteConfirmTargets;
    const n = fileIds.length + folderIds.length;
    if (n === 0 || !isAdmin || deleteBusy) return;
    setDeleteConfirmOpen(false);
    setDeleteBusy(true);
    const folderByIdSnapshot = new Map(folderById);
    try {
      for (const id of fileIds) {
        await api.delete(`/api/files/${id}`);
      }
      const sortedFolders = [...folderIds].sort(
        (a, b) =>
          depthOf(b, folderByIdSnapshot) - depthOf(a, folderByIdSnapshot)
      );
      for (const id of sortedFolders) {
        await api.delete(`/api/folders/${id}`);
      }
      if (browseFolderId && sortedFolders.includes(browseFolderId)) {
        setBrowseFolderIdAndUrl(
          folderByIdSnapshot.get(browseFolderId)?.parentId ?? null
        );
      }
      setExplorerSelection(new Set());
      setSelectedFileId(null);
      void queryClient.invalidateQueries({ queryKey: ['folders', roomId] });
      void queryClient.invalidateQueries({ queryKey: ['files'] });
      void queryClient.invalidateQueries({ queryKey: ['items', roomId] });
    } catch (e) {
      console.error(e);
      window.alert(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
      setDeleteConfirmTargets(null);
    }
  };

  const uploadOneFile = React.useCallback(
    async (file: File, folderIdOverride?: string) => {
      const folderId = folderIdOverride ?? browseFolderId;
      if (!folderId) return;
      const mimeType = file.type || 'application/octet-stream';
      let fileId: string | null = null;
      try {
        const init = await api.post<{
          fileId: string;
          r2Key: string;
          presignedUrl: string;
        }>('/api/files/init-upload', {
          roomId,
          parentId: folderId,
          name: file.name,
          mimeType,
          sizeBytes: file.size
        });
        fileId = init.fileId;
        const put = await api.uploadPut(init.presignedUrl, file, mimeType);
        if (!put.ok) {
          throw new Error(`Upload failed (${put.status})`);
        }
        await api.post('/api/files/confirm-upload', {
          fileId: init.fileId,
          r2Key: init.r2Key
        });
      } catch (e) {
        if (fileId) {
          try {
            await api.delete(`/api/files/${fileId}`);
          } catch {
            // best-effort cleanup of pending row
          }
        }
        throw e;
      }
    },
    [browseFolderId, roomId]
  );

  const runUploads = async (fileList: FileList | File[]) => {
    if (!isAdmin || !browseFolderId) return;
    const arr = Array.from(fileList);
    if (arr.length === 0) return;
    setUploadBusy(true);
    setUploadToast({
      open: true,
      title:
        arr.length === 1
          ? `Uploading “${arr[0]!.name}”…`
          : `Uploading ${arr.length} files…`,
      variant: 'default',
      durationMs: 12_000
    });
    try {
      for (const file of arr) {
        await uploadOneFile(file);
      }
      await queryClient.invalidateQueries({ queryKey: ['items', roomId] });
      setUploadToast({
        open: true,
        title:
          arr.length === 1
            ? 'Upload complete'
            : `${arr.length} uploads complete`,
        variant: 'default',
        durationMs: 4500
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setUploadToast({
        open: true,
        title: 'Upload failed',
        description: msg,
        variant: 'destructive',
        durationMs: 8000
      });
    } finally {
      setUploadBusy(false);
    }
  };

  const runFolderUpload = React.useCallback(
    async (fileList: FileList | File[]) => {
      if (!isAdmin || !browseFolderId) return;
      const files = Array.from(fileList).filter((f) => f.size > 0);
      if (files.length === 0) return;

      // Local maps that persist for the entire batch, separate from React-managed memo maps.
      // This prevents duplicate folder creation when a re-render replaces the memo maps mid-batch.
      const localFolderIdByParentAndName = new Map<string, string>(
        folderIdByParentAndName
      );
      const localFolderById = new Map<string, FolderRow>(folderById);

      const ensureFolderPath = async (
        parts: string[],
        parentId: string | null
      ) => {
        let currentParentId = parentId;
        for (const rawName of parts) {
          const name = rawName.trim();
          if (!name) continue;
          const key = `${currentParentId ?? 'root'}:${name}`;
          const existingId = localFolderIdByParentAndName.get(key);
          if (existingId) {
            currentParentId = existingId;
            continue;
          }
          const created = await api.post<{ folder: FolderRow }>(
            '/api/folders',
            {
              roomId,
              name,
              ...(currentParentId ? { parentId: currentParentId } : {})
            }
          );
          currentParentId = created.folder.id;
          localFolderIdByParentAndName.set(key, created.folder.id);
          localFolderById.set(created.folder.id, created.folder);
        }
        return currentParentId;
      };

      setUploadBusy(true);
      setUploadToast({
        open: true,
        title:
          files.length === 1
            ? `Uploading "${files[0]!.name}"…`
            : `Uploading ${files.length} files from folder…`,
        variant: 'default',
        durationMs: 12_000
      });
      try {
        for (const file of files) {
          const rel =
            (file as File & { webkitRelativePath?: string })
              .webkitRelativePath ?? '';
          const segments = rel.split('/').filter(Boolean);
          const pathParts = segments.length > 1 ? segments.slice(0, -1) : [];
          const targetFolderId =
            pathParts.length > 0
              ? await ensureFolderPath(pathParts, browseFolderId)
              : browseFolderId;

          if (!targetFolderId) continue;
          await uploadOneFile(file, targetFolderId);
        }

        await queryClient.invalidateQueries({ queryKey: ['folders', roomId] });
        await queryClient.invalidateQueries({ queryKey: ['files'] });
        await queryClient.invalidateQueries({ queryKey: ['items', roomId] });
        setUploadToast({
          open: true,
          title:
            files.length === 1
              ? 'Upload complete'
              : `${files.length} uploads complete`,
          variant: 'default',
          durationMs: 4500
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Upload failed';
        setUploadToast({
          open: true,
          title: 'Upload failed',
          description: msg,
          variant: 'destructive',
          durationMs: 8000
        });
      } finally {
        setUploadBusy(false);
      }
    },
    [
      browseFolderId,
      folderById,
      folderIdByParentAndName,
      isAdmin,
      queryClient,
      roomId,
      uploadOneFile
    ]
  );

  const openFolderUpload = React.useCallback(() => {
    if (canUseDirectoryPicker()) {
      setFolderPickerFiles([]);
      setFolderPickerOpen(true);
    } else {
      folderInputRef.current?.click();
    }
  }, []);

  const addFolderFromPicker = React.useCallback(async () => {
    try {
      const picked = await pickDirectoryWithFsApi();
      if (!picked?.length) return;
      setFolderPickerFiles((prev) => [...prev, ...picked]);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      const msg = e instanceof Error ? e.message : 'Could not read folder';
      setUploadToast({
        open: true,
        title: 'Folder selection failed',
        description: msg,
        variant: 'destructive',
        durationMs: 8000
      });
    }
  }, []);

  const confirmFolderPickerUpload = React.useCallback(() => {
    if (folderPickerFiles.length === 0) return;
    const batch = folderPickerFiles;
    setFolderPickerOpen(false);
    setFolderPickerFiles([]);
    void runFolderUpload(batch);
  }, [folderPickerFiles, runFolderUpload]);

  const folderPickerSummary = React.useMemo(() => {
    const roots = new Map<string, number>();
    for (const file of folderPickerFiles) {
      const root = file.webkitRelativePath.split('/')[0] ?? file.name;
      roots.set(root, (roots.get(root) ?? 0) + 1);
    }
    return [...roots.entries()].map(([name, count]) => ({ name, count }));
  }, [folderPickerFiles]);

  const openCreateFolderDialog = React.useCallback(() => {
    setNewFolderName('');
    setCreateFolderOpen(true);
  }, []);

  useRegisterActionBarActions(
    () => [
      {
        id: `room.${roomId}.folder.new`,
        label: 'New folder',
        group: 'Room',
        keywords: ['create', 'add', 'folder'],
        icon: FolderPlus,
        onSelect: openCreateFolderDialog,
        enabled: isAdmin
      },
      {
        id: `room.${roomId}.upload.files`,
        label: 'Upload files',
        group: 'Room',
        keywords: ['upload', 'file'],
        icon: Upload,
        onSelect: () => fileInputRef.current?.click(),
        enabled: isAdmin && browseFolderId !== null
      },
      {
        id: `room.${roomId}.upload.folders`,
        label: 'Upload folder',
        group: 'Room',
        keywords: ['upload', 'folder', 'directory'],
        icon: FolderOpen,
        onSelect: openFolderUpload,
        enabled: isAdmin && browseFolderId !== null
      }
    ],
    [browseFolderId, isAdmin, openCreateFolderDialog, openFolderUpload, roomId]
  );

  const onDropZoneDragEnter = (e: React.DragEvent) => {
    if (!isAdmin || !browseFolderId) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDropActive(true);
  };

  const onDropZoneDragOver = (e: React.DragEvent) => {
    if (!isAdmin || !browseFolderId) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDropZoneDragLeave = (e: React.DragEvent) => {
    if (!isAdmin || !browseFolderId) return;
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setDropActive(false);
  };

  const onDropZoneDrop = (e: React.DragEvent) => {
    if (!isAdmin || !browseFolderId) return;
    e.preventDefault();
    setDropActive(false);
    void (async () => {
      const collected = await collectFilesFromDataTransfer(e.dataTransfer);
      if (collected.length === 0) return;
      if (hasFolderStructure(collected)) {
        await runFolderUpload(collected);
      } else {
        await runUploads(collected);
      }
    })();
  };

  const updateExplorerMarqueeSelection = React.useCallback(
    (rect: MarqueeRect) => {
      const viewport = explorerViewportRef.current;
      if (!viewport) return;

      const visibleKeys = new Set(visibleExplorerKeys);
      const nextDragKeys = new Set<string>();
      for (const item of viewport.querySelectorAll<HTMLElement>(
        '[data-explorer-key]'
      )) {
        const key = item.dataset.explorerKey;
        if (!key || !visibleKeys.has(key)) continue;
        if (rectsIntersect(rect, item.getBoundingClientRect())) {
          nextDragKeys.add(key);
        }
      }

      const start = explorerMarqueeStartRef.current;
      const nextSelection =
        start?.additive === true
          ? new Set([...start.initialSelection, ...nextDragKeys])
          : nextDragKeys;
      explorerMarqueeSelectionRef.current = nextSelection;
      setExplorerSelection((prev) => {
        if (
          prev.size === nextSelection.size &&
          [...prev].every((key) => nextSelection.has(key))
        ) {
          return prev;
        }
        return nextSelection;
      });
    },
    [visibleExplorerKeys]
  );

  const onExplorerPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || visibleExplorerKeys.length === 0) return;
      if (!shouldStartExplorerMarquee(e.target)) return;

      closeContextMenu();
      explorerMarqueePointerIdRef.current = e.pointerId;
      explorerMarqueeDidDragRef.current = false;
      explorerMarqueeSelectionRef.current = new Set(explorerSelection);
      explorerMarqueeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        additive: e.metaKey || e.ctrlKey,
        initialSelection: new Set(explorerSelection)
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [closeContextMenu, explorerSelection, visibleExplorerKeys.length]
  );

  const onExplorerPointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = explorerMarqueeStartRef.current;
      if (!start || explorerMarqueePointerIdRef.current !== e.pointerId) return;

      const distance = Math.hypot(e.clientX - start.x, e.clientY - start.y);
      if (!explorerMarqueeDidDragRef.current && distance < 4) return;

      explorerMarqueeDidDragRef.current = true;
      const rect = marqueeRectFromPoints(
        start.x,
        start.y,
        e.clientX,
        e.clientY
      );
      setExplorerMarqueeRect(rect);
      updateExplorerMarqueeSelection(rect);
      e.preventDefault();
    },
    [updateExplorerMarqueeSelection]
  );

  const finishExplorerMarquee = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (explorerMarqueePointerIdRef.current !== e.pointerId) return;

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }

      if (explorerMarqueeDidDragRef.current) {
        const selectedKeys = explorerMarqueeSelectionRef.current;
        let nextAnchor: string | null = null;
        for (const key of visibleExplorerKeys) {
          if (selectedKeys.has(key)) nextAnchor = key;
        }
        setExplorerSelectionAnchor(nextAnchor);
        suppressExplorerClickRef.current = true;
        window.setTimeout(() => {
          suppressExplorerClickRef.current = false;
        }, 0);
        e.preventDefault();
      }

      explorerMarqueePointerIdRef.current = null;
      explorerMarqueeStartRef.current = null;
      explorerMarqueeDidDragRef.current = false;
      setExplorerMarqueeRect(null);
    },
    [visibleExplorerKeys]
  );

  const onExplorerClickCapture = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!suppressExplorerClickRef.current) return;
      suppressExplorerClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    []
  );

  const roomEmpty =
    !itemsLoading && browseFolderId === null && currentItems.length === 0;
  const atRoomRoot = browseFolderId === null;
  const inFolderEmpty =
    browseFolderId !== null &&
    !itemsLoading &&
    childFolders.length === 0 &&
    files.length === 0;

  React.useEffect(() => {
    const isEditableTarget = (t: EventTarget | null) => {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select';
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (isEditableTarget(e.target)) return;

      const isMac =
        typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if (!cmdOrCtrl) return;

      // Cmd/Ctrl+I → toggle details panel
      if (e.key.toLowerCase() === 'i') {
        e.preventDefault();
        setDetailsOpen((v) => !v);
        return;
      }

      // Admin-only actions.
      if (!isAdmin) return;

      // Cmd/Ctrl+Shift+N → new folder
      if (e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        openCreateFolderDialog();
        return;
      }

      // Upload shortcuts only make sense inside a folder.
      if (browseFolderId === null) return;

      // Cmd/Ctrl+U → upload files
      if (!e.shiftKey && e.key.toLowerCase() === 'u') {
        e.preventDefault();
        fileInputRef.current?.click();
        return;
      }

      // Cmd/Ctrl+Shift+U → upload folder
      if (e.shiftKey && e.key.toLowerCase() === 'u') {
        e.preventDefault();
        openFolderUpload();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [browseFolderId, isAdmin, openCreateFolderDialog, openFolderUpload]);

  const archiveProgress =
    archiveJob && archiveJob.totalBytes > 0
      ? Math.min(
          100,
          Math.round((archiveJob.processedBytes / archiveJob.totalBytes) * 100)
        )
      : archiveJob?.status === 'completed'
        ? 100
        : 0;
  const archiveStatusLabel =
    archiveJob?.status === 'completed'
      ? 'Ready'
      : archiveJob?.status === 'failed'
        ? 'Failed'
        : archiveJob?.status === 'expired'
          ? 'Expired'
          : archiveJob?.status === 'processing'
            ? 'Building archive'
            : archiveBusy
              ? 'Starting archive job'
              : 'Preparing archive';

  return (
    <>
      <Toast
        open={uploadToast.open}
        onOpenChange={(open) => setUploadToast((t) => ({ ...t, open }))}
        title={uploadToast.title}
        description={uploadToast.description}
        variant={uploadToast.variant}
        durationMs={uploadToast.durationMs ?? 4500}
      />
      <Dialog
        open={folderPickerOpen}
        onOpenChange={(open) => {
          setFolderPickerOpen(open);
          if (!open) setFolderPickerFiles([]);
        }}
      >
        <DialogPanel aria-labelledby="folder-upload-title">
          <div className="space-y-4">
            <div>
              <h2 id="folder-upload-title" className="text-lg font-semibold">
                Upload folders
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose one or more folders, or drag folders into the room to
                skip the browser upload confirmation.
              </p>
            </div>
            {folderPickerSummary.length > 0 ? (
              <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border bg-muted/30 px-3 py-2 text-sm">
                {folderPickerSummary.map(({ name, count }) => (
                  <li key={name} className="flex justify-between gap-3">
                    <span className="truncate font-medium">{name}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {count} {count === 1 ? 'file' : 'files'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFolderPickerOpen(false);
                  setFolderPickerFiles([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void addFolderFromPicker()}
              >
                {folderPickerFiles.length > 0
                  ? 'Add another folder'
                  : 'Choose folder'}
              </Button>
              <Button
                type="button"
                disabled={folderPickerFiles.length === 0 || uploadBusy}
                onClick={confirmFolderPickerUpload}
              >
                Upload {folderPickerFiles.length}{' '}
                {folderPickerFiles.length === 1 ? 'file' : 'files'}
              </Button>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogPanel aria-labelledby="archive-title">
          <div className="space-y-4">
            <div>
              <h2 id="archive-title" className="text-lg font-semibold">
                Bulk ZIP download
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {archiveStatusLabel}. The download will start automatically when
                the ZIP is ready.
              </p>
            </div>
            <div className="space-y-2">
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${archiveProgress}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {archiveJob
                    ? `${archiveJob.processedFileCount}/${archiveJob.fileCount} files`
                    : `${selectionInfo.count} selected item(s)`}
                </span>
                <span>{archiveProgress}%</span>
              </div>
            </div>
            {archiveJob?.error ? (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {archiveJob.error}
              </p>
            ) : null}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setArchiveDialogOpen(false)}
              >
                Hide
              </Button>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
      <div className="flex h-[calc(100vh-3.5rem)] min-h-0 flex-col">
        {!roomEmpty && (
          <div className="flex flex-col border-b bg-card">
            <div className="flex flex-col gap-2 px-3 pt-3 pb-2 sm:px-4 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
              <nav aria-label="Folder path" className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center overflow-x-auto overscroll-x-contain sm:hidden">
                  <div className="flex w-max items-center gap-1">
                    {breadcrumb.map((item, i) => (
                      <React.Fragment key={item.id ?? `room-root-${i}`}>
                        {i > 0 && (
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                        )}
                        {i === breadcrumb.length - 1 ? (
                          <span className="shrink-0 px-1.5 py-0.5 text-sm font-medium text-foreground">
                            {item.label}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setBrowseFolderIdAndUrl(item.id);
                              setSelectedFileId(null);
                            }}
                            className="shrink-0 cursor-pointer rounded-md px-1.5 py-0.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                          >
                            {item.label}
                          </button>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="hidden min-w-0 items-center gap-1 overflow-hidden sm:flex">
                  {breadcrumbItems.map((item, i) => (
                    <React.Fragment
                      key={
                        item.type === 'ellipsis'
                          ? `ellipsis-${i}`
                          : (item.id ?? `room-root-${i}`)
                      }
                    >
                      {i > 0 &&
                        item.type !== 'ellipsis' &&
                        breadcrumbItems[i - 1]?.type !== 'ellipsis' && (
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                        )}
                      {item.type === 'ellipsis' ? (
                        <div className="flex shrink-0 items-center gap-1">
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="cursor-pointer select-none rounded-md px-1.5 py-0.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                aria-label="Collapsed path"
                              >
                                …
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="start"
                              className="min-w-[14rem]"
                            >
                              <DropdownMenuLabel>Folders</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {collapsedBreadcrumb.map((c) => (
                                <DropdownMenuItem
                                  key={c.id ?? 'root'}
                                  className="max-w-[18rem] cursor-pointer"
                                  onClick={() => {
                                    setBrowseFolderIdAndUrl(c.id);
                                    setSelectedFileId(null);
                                  }}
                                >
                                  <span className="truncate">{c.label}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                        </div>
                      ) : i === breadcrumbItems.length - 1 ? (
                        <span className="min-w-0 max-w-[40vw] truncate px-1.5 py-0.5 text-sm font-medium text-foreground md:max-w-[16rem]">
                          {item.label}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setBrowseFolderIdAndUrl(item.id);
                            setSelectedFileId(null);
                          }}
                          className="min-w-0 max-w-[10rem] shrink cursor-pointer truncate rounded-md px-1.5 py-0.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:max-w-[12rem]"
                        >
                          {item.label}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </nav>

              <div className="flex flex-wrap items-center justify-end gap-2 lg:shrink-0">
                <div className="flex flex-wrap items-center gap-2">
                  {isAdmin && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const fl = e.target.files;
                          if (fl?.length) void runUploads(fl);
                          e.target.value = '';
                        }}
                      />
                      <input
                        ref={folderInputRef}
                        type="file"
                        // @ts-expect-error - non-standard attribute supported by Chromium/WebKit
                        webkitdirectory=""
                        className="hidden"
                        onChange={(e) => {
                          const fl = e.target.files;
                          if (fl?.length) void runFolderUpload(fl);
                          e.target.value = '';
                        }}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                          >
                            <Plus className="h-4 w-4" aria-hidden />
                            New
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="min-w-[12.5rem]"
                        >
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            Upload
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            disabled={atRoomRoot || uploadBusy}
                            className="gap-2"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="h-4 w-4 shrink-0" aria-hidden />
                            {uploadBusy ? 'Uploading…' : 'Upload files'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={atRoomRoot || uploadBusy}
                            className="gap-2"
                            onClick={openFolderUpload}
                          >
                            <FolderOpen
                              className="h-4 w-4 shrink-0"
                              aria-hidden
                            />
                            {uploadBusy ? 'Uploading…' : 'Upload folder'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            Create
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            className="gap-2"
                            onClick={openCreateFolderDialog}
                          >
                            <FolderPlus
                              className="h-4 w-4 shrink-0"
                              aria-hidden
                            />
                            New folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2"
                            onClick={() => setCreateGroupOpen(true)}
                          >
                            <DoorOpen
                              className="h-4 w-4 shrink-0"
                              aria-hidden
                            />
                            New group
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        type="button"
                        size="sm"
                        className="gap-1.5"
                        disabled={!canOpenFolderPermissionsFromMenu}
                        onClick={() => {
                          if (!folderPermissionsMenuTarget) return;
                          openSharePermissionsDialog(
                            folderPermissionsMenuTarget
                          );
                        }}
                      >
                        <Share className="h-4 w-4" aria-hidden />
                        Share
                      </Button>
                    </>
                  )}
                  {!isAdmin && (
                    <Button
                      type="button"
                      size="sm"
                      className="gap-1.5"
                      disabled={archiveBusy}
                      onClick={() => handleDownloadSelected()}
                    >
                      <Download className="h-4 w-4" aria-hidden />
                      {bulkDownloadLabel}
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        aria-label="More actions"
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[12rem]">
                      {isAdmin && (
                        <DropdownMenuItem
                          className="gap-2"
                          disabled={archiveBusy}
                          onClick={() => handleDownloadSelected()}
                        >
                          <Download className="h-4 w-4 shrink-0" aria-hidden />
                          {bulkDownloadLabel}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => setDetailsOpen((v) => !v)}
                      >
                        <Info className="h-4 w-4 shrink-0" aria-hidden />
                        {detailsOpen ? 'Hide details' : 'Show details'}
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="gap-2"
                            disabled={
                              !canOpenRenumber || renumberMutation.isPending
                            }
                            onClick={openRenumberDialog}
                          >
                            <Hash className="h-4 w-4 shrink-0" aria-hidden />
                            Renumber folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2"
                            disabled={!canOpenFolderPermissionsFromMenu}
                            onClick={() => {
                              if (!folderPermissionsMenuTarget) return;
                              openSharePermissionsDialog(
                                folderPermissionsMenuTarget
                              );
                            }}
                          >
                            <Users className="h-4 w-4 shrink-0" aria-hidden />
                            Who can view
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2"
                            disabled={
                              deleteTargetCount === 0 || moveMutation.isPending
                            }
                            onClick={openMoveDialog}
                          >
                            <Move className="h-4 w-4 shrink-0" aria-hidden />
                            Move selected
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2 text-destructive focus:text-destructive"
                            disabled={deleteBusy || deleteTargetCount === 0}
                            onClick={() => openDeleteConfirm()}
                          >
                            <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                            {deleteBusy
                              ? 'Deleting…'
                              : deleteTargetCount === 0
                                ? 'Delete'
                                : `Delete ${deleteTargetCount} selected`}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border/60 px-4 py-2">
              <div className="flex shrink-0 items-center gap-2">
                {viewMode === 'grid' && (
                  <div className="flex items-center gap-0.5 rounded-md border bg-background px-1 py-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      aria-label="Smaller thumbnails"
                      disabled={gridThumbStep <= 1}
                      onClick={() => bumpThumbStep(-1)}
                    >
                      <Minus className="h-3 w-3" aria-hidden />
                    </Button>
                    <input
                      type="range"
                      min={1}
                      max={THUMB_STEPS}
                      step={1}
                      value={gridThumbStep}
                      aria-label="Thumbnail size"
                      className="h-1.5 w-14 cursor-pointer accent-primary"
                      onChange={(e) => {
                        const n = Number.parseInt(e.target.value, 10);
                        if (n >= 1 && n <= THUMB_STEPS) {
                          setGridThumbStep(n);
                          try {
                            localStorage.setItem(THUMB_STEP_KEY, String(n));
                          } catch {
                            /* ignore */
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      aria-label="Larger thumbnails"
                      disabled={gridThumbStep >= THUMB_STEPS}
                      onClick={() => bumpThumbStep(1)}
                    >
                      <Plus className="h-3 w-3" aria-hidden />
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label={
                    viewMode === 'list'
                      ? 'Switch to grid view'
                      : 'Switch to list view'
                  }
                  onClick={() =>
                    handleViewModeChange(viewMode === 'list' ? 'grid' : 'list')
                  }
                >
                  {viewMode === 'list' ? (
                    <Grid3X3 className="h-4 w-4" aria-hidden />
                  ) : (
                    <List className="h-4 w-4" aria-hidden />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          {/* Main content */}
          <main
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            style={
              !roomEmpty
                ? ({
                    ['--list-thumb' as string]: `${listThumbPx}px`
                  } as React.CSSProperties)
                : undefined
            }
          >
            <div
              ref={explorerViewportRef}
              className={cn(
                'relative flex min-h-0 flex-1 flex-col overflow-auto',
                explorerMarqueeRect && 'select-none'
              )}
              onPointerDown={onExplorerPointerDown}
              onPointerMove={onExplorerPointerMove}
              onPointerUp={finishExplorerMarquee}
              onPointerCancel={finishExplorerMarquee}
              onClickCapture={onExplorerClickCapture}
              onDragEnter={onDropZoneDragEnter}
              onDragOver={onDropZoneDragOver}
              onDragLeave={onDropZoneDragLeave}
              onDrop={onDropZoneDrop}
            >
              {explorerMarqueeRect && (
                <div
                  className="pointer-events-none fixed z-20 rounded-sm border border-primary/60 bg-primary/15"
                  style={explorerMarqueeRect}
                  aria-hidden
                />
              )}
              {dropActive && isAdmin && browseFolderId && (
                <div
                  className="pointer-events-none absolute inset-3 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/10 backdrop-blur-[1px]"
                  aria-hidden
                >
                  <div className="rounded-lg bg-background/90 px-6 py-4 text-center shadow-sm">
                    <Upload
                      className="mx-auto h-10 w-10 text-primary"
                      aria-hidden
                    />
                    <p className="mt-2 font-medium text-foreground">
                      Drop files or folders to upload
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Drag one or more folders to keep their structure
                    </p>
                  </div>
                </div>
              )}

              <div className="flex-1 p-4">
                {itemsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : roomEmpty ? (
                  <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed bg-card/40 px-6 py-16 text-center">
                    <FolderOpen
                      className="h-12 w-12 text-muted-foreground"
                      aria-hidden
                    />
                    <p className="mt-4 text-muted-foreground">No folders yet</p>
                    {isAdmin ? (
                      <>
                        <p className="mt-1 max-w-md text-sm text-muted-foreground">
                          Create your first folder to organize this room. Files
                          belong inside folders—not at the room root—so
                          permissions and navigation stay clear.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                          <Button
                            type="button"
                            className="gap-2"
                            onClick={() => {
                              setNewFolderName('');
                              setCreateFolderOpen(true);
                            }}
                          >
                            <FolderPlus
                              className="h-4 w-4 shrink-0"
                              aria-hidden
                            />
                            New folder
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="gap-2"
                            onClick={() => setCreateGroupOpen(true)}
                          >
                            <DoorOpen
                              className="h-4 w-4 shrink-0"
                              aria-hidden
                            />
                            New group
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="gap-2"
                            onClick={() => setInviteRoomViewerOpen(true)}
                          >
                            <Share className="h-4 w-4 shrink-0" aria-hidden />
                            Share
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        An admin can add folders to this room.
                      </p>
                    )}
                  </div>
                ) : atRoomRoot ? (
                  <div className="space-y-6">
                    {childFolders.length === 0 && (
                      <div className="rounded-xl border border-dashed bg-muted/30 px-6 py-10 text-center">
                        <FolderOpen
                          className="mx-auto h-10 w-10 text-muted-foreground/80"
                          aria-hidden
                        />
                        <p className="mt-3 font-medium text-foreground">
                          Top-level folders
                        </p>
                        <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
                          Open a folder to add files and subfolders. Keeping
                          uploads inside folders makes the dataroom easier to
                          browse and manage permissions for.
                        </p>
                      </div>
                    )}
                    {childFolders.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center text-sm text-muted-foreground">
                        <p>No top-level folders yet.</p>
                        {isAdmin && (
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-4 gap-2"
                            onClick={() => {
                              setNewFolderName('');
                              setCreateFolderOpen(true);
                            }}
                          >
                            <FolderPlus className="h-4 w-4" aria-hidden />
                            Create the first folder
                          </Button>
                        )}
                      </div>
                    ) : viewMode === 'list' ? (
                      <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                        <ExplorerListColgroup />
                        <ExplorerListThead />
                        <tbody>
                          {childFolders.map((folder) => {
                            const selected = explorerSelection.has(
                              explorerFolderKey(folder.id)
                            );
                            return (
                              <tr
                                key={folder.id}
                                data-explorer-key={explorerFolderKey(folder.id)}
                                tabIndex={0}
                                aria-selected={selected}
                                className={explorerListRowClass(selected)}
                                onClick={(e) => {
                                  if (e.detail >= 2) {
                                    e.preventDefault();
                                    setBrowseFolderIdAndUrl(folder.id);
                                    setSelectedFileId(null);
                                    return;
                                  }
                                  selectKey(explorerFolderKey(folder.id), e);
                                }}
                                onDoubleClick={() => {
                                  setBrowseFolderIdAndUrl(folder.id);
                                  setSelectedFileId(null);
                                }}
                                onContextMenu={(e) =>
                                  openExplorerContextMenu(
                                    e,
                                    {
                                      type: 'folder',
                                      id: folder.id,
                                      label: itemLabelWithDisplayNumber(
                                        folder.displayNumber,
                                        folder.name,
                                        activeNumberingSeparator
                                      )
                                    },
                                    explorerFolderKey(folder.id)
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setBrowseFolderIdAndUrl(folder.id);
                                    setSelectedFileId(null);
                                  }
                                }}
                              >
                                <td
                                  className={cn(
                                    EXPLORER_LIST_FIRST_CELL_CLASS,
                                    'pr-4'
                                  )}
                                >
                                  <ExplorerItemThumb
                                    sizePx={listThumbPx}
                                    folder
                                    mimeType={undefined}
                                  />
                                </td>
                                <td className={EXPLORER_LIST_NAME_CELL_CLASS}>
                                  {itemLabelWithDisplayNumber(
                                    folder.displayNumber,
                                    folder.name,
                                    activeNumberingSeparator
                                  )}
                                </td>
                                <td
                                  className={cn(
                                    EXPLORER_LIST_MIDDLE_CELL_CLASS,
                                    'truncate'
                                  )}
                                >
                                  {formatUpdatedAt(folder.createdAt)}
                                </td>
                                <td
                                  className={cn(
                                    EXPLORER_LIST_MIDDLE_CELL_CLASS,
                                    'text-right tabular-nums'
                                  )}
                                />
                                <td
                                  className={cn(
                                    EXPLORER_LIST_LAST_CELL_CLASS,
                                    'text-right'
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExplorerRowActionsButton
                                    label={itemLabelWithDisplayNumber(
                                      folder.displayNumber,
                                      folder.name,
                                      activeNumberingSeparator
                                    )}
                                    selected={selected}
                                    onOpenMenu={(e) =>
                                      openExplorerContextMenu(
                                        e,
                                        {
                                          type: 'folder',
                                          id: folder.id,
                                          label: itemLabelWithDisplayNumber(
                                            folder.displayNumber,
                                            folder.name,
                                            activeNumberingSeparator
                                          )
                                        },
                                        explorerFolderKey(folder.id),
                                        { anchor: 'trigger' }
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div
                        className="grid gap-4"
                        style={{
                          gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(gridThumbPx * 1.35) + 48}px, 1fr))`
                        }}
                      >
                        {childFolders.map((folder) => (
                          <button
                            key={folder.id}
                            data-explorer-key={explorerFolderKey(folder.id)}
                            type="button"
                            aria-pressed={explorerSelection.has(
                              explorerFolderKey(folder.id)
                            )}
                            onClick={(e) =>
                              selectKey(explorerFolderKey(folder.id), e)
                            }
                            onDoubleClick={() => {
                              setBrowseFolderIdAndUrl(folder.id);
                              setSelectedFileId(null);
                            }}
                            onContextMenu={(e) =>
                              openExplorerContextMenu(
                                e,
                                {
                                  type: 'folder',
                                  id: folder.id,
                                  label: itemLabelWithDisplayNumber(
                                    folder.displayNumber,
                                    folder.name,
                                    activeNumberingSeparator
                                  )
                                },
                                explorerFolderKey(folder.id)
                              )
                            }
                            className={explorerGridItemClass(
                              explorerSelection.has(
                                explorerFolderKey(folder.id)
                              )
                            )}
                          >
                            <div className="flex w-full items-center justify-center">
                              <ExplorerItemThumb
                                sizePx={gridThumbPx}
                                folder
                                mimeType={undefined}
                              />
                            </div>
                            <p className="mt-2 w-full truncate text-xs font-medium">
                              {itemLabelWithDisplayNumber(
                                folder.displayNumber,
                                folder.name,
                                activeNumberingSeparator
                              )}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : itemsLoading ? (
                  viewMode === 'list' ? (
                    <div className="space-y-2">
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                      <Skeleton className="aspect-square" />
                      <Skeleton className="aspect-square" />
                    </div>
                  )
                ) : (
                  <div className="space-y-4">
                    {viewMode === 'list' ? (
                      inFolderEmpty ? (
                        <div className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed bg-card/40 py-12 text-center">
                          <FileText
                            className="h-12 w-12 text-muted-foreground/80"
                            aria-hidden
                          />
                          <p className="mt-4 text-muted-foreground">
                            This folder is empty
                          </p>
                          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                            {isAdmin
                              ? 'Upload files here or add a subfolder to keep things organized.'
                              : 'Files will appear here when an admin uploads them.'}
                          </p>
                          {isAdmin && (
                            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                onClick={() => {
                                  setNewFolderName('');
                                  setCreateFolderOpen(true);
                                }}
                              >
                                <FolderPlus className="h-4 w-4" aria-hidden />
                                New subfolder
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="gap-1.5"
                                disabled={uploadBusy}
                                onClick={() => fileInputRef.current?.click()}
                              >
                                <Upload className="h-4 w-4" aria-hidden />
                                Upload files
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                          <ExplorerListColgroup />
                          <ExplorerListThead />
                          <tbody>
                            {childFolders.map((folder) => {
                              const selected = explorerSelection.has(
                                explorerFolderKey(folder.id)
                              );
                              return (
                                <tr
                                  key={folder.id}
                                  data-explorer-key={explorerFolderKey(
                                    folder.id
                                  )}
                                  tabIndex={0}
                                  aria-selected={selected}
                                  className={explorerListRowClass(selected)}
                                  onClick={(e) => {
                                    if (e.detail >= 2) {
                                      e.preventDefault();
                                      setBrowseFolderIdAndUrl(folder.id);
                                      setSelectedFileId(null);
                                      return;
                                    }
                                    selectKey(explorerFolderKey(folder.id), e);
                                  }}
                                  onDoubleClick={() => {
                                    setBrowseFolderIdAndUrl(folder.id);
                                    setSelectedFileId(null);
                                  }}
                                  onContextMenu={(e) =>
                                    openExplorerContextMenu(
                                      e,
                                      {
                                        type: 'folder',
                                        id: folder.id,
                                        label: itemLabelWithDisplayNumber(
                                          folder.displayNumber,
                                          folder.name,
                                          activeNumberingSeparator
                                        )
                                      },
                                      explorerFolderKey(folder.id)
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setBrowseFolderIdAndUrl(folder.id);
                                      setSelectedFileId(null);
                                    }
                                  }}
                                >
                                  <td
                                    className={EXPLORER_LIST_FIRST_CELL_CLASS}
                                  >
                                    <ExplorerItemThumb
                                      sizePx={listThumbPx}
                                      folder
                                      mimeType={undefined}
                                    />
                                  </td>
                                  <td className={EXPLORER_LIST_NAME_CELL_CLASS}>
                                    {itemLabelWithDisplayNumber(
                                      folder.displayNumber,
                                      folder.name,
                                      activeNumberingSeparator
                                    )}
                                  </td>
                                  <td
                                    className={cn(
                                      EXPLORER_LIST_MIDDLE_CELL_CLASS,
                                      'truncate'
                                    )}
                                  >
                                    {formatUpdatedAt(folder.createdAt)}
                                  </td>
                                  <td
                                    className={cn(
                                      EXPLORER_LIST_MIDDLE_CELL_CLASS,
                                      'text-right tabular-nums'
                                    )}
                                  />
                                  <td
                                    className={cn(
                                      EXPLORER_LIST_LAST_CELL_CLASS,
                                      'text-right'
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExplorerRowActionsButton
                                      label={itemLabelWithDisplayNumber(
                                        folder.displayNumber,
                                        folder.name,
                                        activeNumberingSeparator
                                      )}
                                      selected={selected}
                                      onOpenMenu={(e) =>
                                        openExplorerContextMenu(
                                          e,
                                          {
                                            type: 'folder',
                                            id: folder.id,
                                            label: itemLabelWithDisplayNumber(
                                              folder.displayNumber,
                                              folder.name,
                                              activeNumberingSeparator
                                            )
                                          },
                                          explorerFolderKey(folder.id),
                                          { anchor: 'trigger' }
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                            {files.map((file) =>
                              (() => {
                                const selected = explorerSelection.has(
                                  explorerFileKey(file.id)
                                );
                                return (
                                  <tr
                                    key={file.id}
                                    data-explorer-key={explorerFileKey(file.id)}
                                    tabIndex={0}
                                    aria-selected={selected}
                                    className={explorerListRowClass(selected)}
                                    onClick={(e) => {
                                      if (e.detail >= 2) {
                                        e.preventDefault();
                                        openFilePreview(file);
                                        return;
                                      }
                                      selectKey(explorerFileKey(file.id), e);
                                    }}
                                    onDoubleClick={() => {
                                      openFilePreview(file);
                                    }}
                                    onContextMenu={(e) =>
                                      openExplorerContextMenu(
                                        e,
                                        {
                                          type: 'file',
                                          id: file.id,
                                          label: file.name
                                        },
                                        explorerFileKey(file.id)
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (e.key === 'Enter') {
                                          openFilePreview(file);
                                        }
                                      }
                                    }}
                                  >
                                    <td
                                      className={EXPLORER_LIST_FIRST_CELL_CLASS}
                                    >
                                      <ExplorerItemThumb
                                        sizePx={listThumbPx}
                                        folder={false}
                                        mimeType={file.mimeType}
                                        fileId={file.id}
                                      />
                                    </td>
                                    <td
                                      className={EXPLORER_LIST_NAME_CELL_CLASS}
                                    >
                                      {displayNumberPrefix(
                                        file.displayNumber,
                                        activeNumberingSeparator
                                      )}
                                      {file.name}
                                    </td>
                                    <td
                                      className={cn(
                                        EXPLORER_LIST_MIDDLE_CELL_CLASS,
                                        'truncate'
                                      )}
                                    >
                                      {formatUpdatedAt(file.updatedAt)}
                                    </td>
                                    <td
                                      data-explorer-no-marquee
                                      className={cn(
                                        EXPLORER_LIST_MIDDLE_CELL_CLASS,
                                        'text-right tabular-nums'
                                      )}
                                    >
                                      {formatBytes(file.sizeBytes)}
                                    </td>
                                    <td
                                      className={cn(
                                        EXPLORER_LIST_LAST_CELL_CLASS,
                                        'text-right'
                                      )}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExplorerRowActionsButton
                                        label={file.name}
                                        selected={
                                          selected || selectedFileId === file.id
                                        }
                                        onOpenMenu={(e) =>
                                          openExplorerContextMenu(
                                            e,
                                            {
                                              type: 'file',
                                              id: file.id,
                                              label: file.name
                                            },
                                            explorerFileKey(file.id),
                                            { anchor: 'trigger' }
                                          )
                                        }
                                      />
                                    </td>
                                  </tr>
                                );
                              })()
                            )}
                          </tbody>
                        </table>
                      )
                    ) : inFolderEmpty ? (
                      <div className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed bg-card/40 py-12 text-center">
                        <FileText
                          className="h-12 w-12 text-muted-foreground/80"
                          aria-hidden
                        />
                        <p className="mt-4 text-muted-foreground">
                          This folder is empty
                        </p>
                        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                          {isAdmin
                            ? 'Upload files here or add a subfolder to keep things organized.'
                            : 'Files will appear here when an admin uploads them.'}
                        </p>
                        {isAdmin && (
                          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1.5"
                              onClick={() => {
                                setNewFolderName('');
                                setCreateFolderOpen(true);
                              }}
                            >
                              <FolderPlus className="h-4 w-4" aria-hidden />
                              New subfolder
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="gap-1.5"
                              disabled={uploadBusy}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="h-4 w-4" aria-hidden />
                              Upload files
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className="grid gap-4"
                        style={{
                          gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(gridThumbPx * 1.35) + 48}px, 1fr))`
                        }}
                      >
                        {gridItems.map((item) => {
                          if (item.kind === 'folder') {
                            const selected = explorerSelection.has(
                              explorerFolderKey(item.id)
                            );
                            return (
                              <button
                                key={item.id}
                                data-explorer-key={explorerFolderKey(item.id)}
                                type="button"
                                aria-pressed={selected}
                                onClick={(e) =>
                                  selectKey(explorerFolderKey(item.id), e)
                                }
                                onDoubleClick={() => {
                                  setBrowseFolderIdAndUrl(item.id);
                                  setSelectedFileId(null);
                                }}
                                onContextMenu={(e) =>
                                  openExplorerContextMenu(
                                    e,
                                    {
                                      type: 'folder',
                                      id: item.id,
                                      label: itemLabelWithDisplayNumber(
                                        item.displayNumber,
                                        item.name,
                                        activeNumberingSeparator
                                      )
                                    },
                                    explorerFolderKey(item.id)
                                  )
                                }
                                className={explorerGridItemClass(selected)}
                              >
                                <div className="flex w-full items-center justify-center">
                                  <ExplorerItemThumb
                                    sizePx={gridThumbPx}
                                    folder
                                    mimeType={undefined}
                                  />
                                </div>
                                <p className="mt-2 w-full truncate text-xs font-medium">
                                  {itemLabelWithDisplayNumber(
                                    item.displayNumber,
                                    item.name,
                                    activeNumberingSeparator
                                  )}
                                </p>
                              </button>
                            );
                          }

                          const selected = explorerSelection.has(
                            explorerFileKey(item.id)
                          );
                          return (
                            <button
                              key={item.id}
                              data-explorer-key={explorerFileKey(item.id)}
                              type="button"
                              aria-pressed={selected}
                              onClick={(e) => {
                                setSelectedFileId(item.id);
                                selectKey(explorerFileKey(item.id), e);
                              }}
                              onDoubleClick={() => {
                                openFilePreview(item);
                              }}
                              onContextMenu={(e) =>
                                openExplorerContextMenu(
                                  e,
                                  {
                                    type: 'file',
                                    id: item.id,
                                    label: item.name
                                  },
                                  explorerFileKey(item.id)
                                )
                              }
                              className={explorerGridItemClass(selected)}
                            >
                              <div className="flex w-full items-center justify-center">
                                <ExplorerItemThumb
                                  sizePx={gridThumbPx}
                                  folder={false}
                                  mimeType={item.mimeType}
                                  fileId={item.id}
                                />
                              </div>
                              <div className="mt-2 flex w-full min-w-0 items-center gap-1">
                                <p className="min-w-0 flex-1 truncate text-xs font-medium">
                                  {displayNumberPrefix(
                                    item.displayNumber,
                                    activeNumberingSeparator
                                  )}
                                  {item.name}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </main>

          {/* Room info, folder tree, file details — right rail */}
          {detailsOpen && (
            <aside className="hidden w-72 shrink-0 flex-col border-l bg-card lg:flex xl:w-80">
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-semibold">Details</h4>
                    <button
                      type="button"
                      onClick={() => setDetailsOpen(false)}
                      className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent"
                      aria-label="Close details panel"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {selectedFile ? (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <FileText className="h-10 w-10 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="break-all font-medium">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {selectedFile.mimeType}
                          </p>
                        </div>
                      </div>
                      <Separator />
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Size</span>
                          <span>{formatBytes(selectedFile.sizeBytes)}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Version</span>
                          <span>v{selectedFile.currentVersion}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Created</span>
                          <span>
                            {new Date(
                              selectedFile.createdAt
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Updated</span>
                          <span>
                            {new Date(
                              selectedFile.updatedAt
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => void handleDownload(selectedFile.id)}
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  ) : selectionInfo.count > 1 ? (
                    <div className="w-full space-y-3 rounded-lg border bg-muted/30 p-3 text-left">
                      <p className="font-medium text-foreground">
                        {selectionInfo.count} items selected
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {selectionInfo.folderIds.length > 0 && (
                          <span>
                            {selectionInfo.folderIds.length} folder
                            {selectionInfo.folderIds.length === 1 ? '' : 's'}
                          </span>
                        )}
                        {selectionInfo.fileIds.length > 0 && (
                          <span>
                            {selectionInfo.fileIds.length} file
                            {selectionInfo.fileIds.length === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Use the context menu or toolbar actions to operate on
                        the selection.
                      </p>
                    </div>
                  ) : selectedSingleFolder ? (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <FolderOpen className="h-10 w-10 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="break-words font-medium">
                            {itemLabelWithDisplayNumber(
                              selectedSingleFolder.displayNumber,
                              selectedSingleFolder.name,
                              activeNumberingSeparator
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Folder
                          </p>
                        </div>
                      </div>
                      <Separator />
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">Created</span>
                          <span>
                            {formatUpdatedAt(selectedSingleFolder.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center py-6 text-center text-sm text-muted-foreground">
                      <FileText className="mb-2 h-8 w-8 opacity-50" />
                      <p>Select a file or folder in the list to see details.</p>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      <Dialog
        open={createFolderOpen}
        onOpenChange={(open) => {
          setCreateFolderOpen(open);
          if (!open) setNewFolderName('');
        }}
      >
        <DialogPanel aria-labelledby={newFolderTitleId}>
          <h2
            id={newFolderTitleId}
            className="text-lg font-semibold leading-none tracking-tight"
          >
            New folder
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {roomEmpty
              ? 'Create the first folder in this room.'
              : atRoomRoot
                ? 'Create a top-level folder in this room.'
                : 'Create a subfolder inside the current folder.'}
          </p>
          <form
            className="mt-4 space-y-4"
            onSubmit={(ev) => {
              ev.preventDefault();
              const name = newFolderName.trim();
              if (!name || createFolderMutation.isPending) return;
              createFolderMutation.mutate(name);
            }}
          >
            <div className="space-y-2">
              <label htmlFor="new-folder-name" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="new-folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. Due diligence"
                autoComplete="off"
                data-1p-ignore
                data-form-type="other"
                autoFocus
                disabled={createFolderMutation.isPending}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={createFolderMutation.isPending}
                onClick={() => {
                  setCreateFolderOpen(false);
                  setNewFolderName('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !newFolderName.trim() || createFolderMutation.isPending
                }
              >
                {createFolderMutation.isPending ? 'Creating…' : 'Create folder'}
              </Button>
            </div>
          </form>
        </DialogPanel>
      </Dialog>

      <Dialog
        open={renumberOpen}
        onOpenChange={(open) => {
          setRenumberOpen(open);
          if (!open) {
            setRenumberDragKey(null);
          }
        }}
      >
        <DialogPanel className="max-w-2xl" aria-labelledby="renumber-title">
          <h2
            id="renumber-title"
            className="text-lg font-semibold leading-none tracking-tight"
          >
            Renumber folder
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Drag items into the order you want. The numbering format applies to
            this level.
          </p>

          <form
            className="mt-4 flex flex-col gap-4"
            onSubmit={async (ev) => {
              ev.preventDefault();
              if (renumberItems.length === 0 || renumberMutation.isPending)
                return;
              const numberingConfig = numberingConfigFromStyle({
                format: renumberFormat,
                padding: renumberPadding,
                casing: renumberCasing,
                separator: activeNumberingStyle.separator
              });
              try {
                await renumberMutation.mutateAsync({
                  items: renumberItems.map(({ type, id }) => ({ type, id })),
                  numberingConfig
                });
                setRenumberOpen(false);
                setRenumberDragKey(null);
              } catch (e) {
                console.error(e);
                window.alert(
                  e instanceof Error ? e.message : 'Renumber failed'
                );
              }
            }}
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_7rem]">
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Format
                <Select
                  value={renumberFormat}
                  onValueChange={(value) =>
                    setRenumberFormat(value as RenumberFormat)
                  }
                  disabled={renumberMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="numeric">Numeric</SelectItem>
                      <SelectItem value="roman">Roman numerals</SelectItem>
                      <SelectItem value="letter">Letters</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </label>
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Casing
                <Select
                  value={renumberCasing}
                  onValueChange={(value) =>
                    setRenumberCasing(value as NumberingCasing)
                  }
                  disabled={
                    renumberMutation.isPending || isNumericRenumberFormat
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="upper">Uppercase</SelectItem>
                      <SelectItem value="lower">Lowercase</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </label>
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Padding
                <Input
                  type="number"
                  min={0}
                  max={6}
                  value={renumberPadding}
                  onChange={(e) => {
                    const n = Number.parseInt(e.target.value, 10);
                    setRenumberPadding(
                      Number.isFinite(n) ? Math.min(6, Math.max(0, n)) : 0
                    );
                  }}
                  disabled={
                    renumberMutation.isPending || !isNumericRenumberFormat
                  }
                />
              </label>
            </div>

            <div className="max-h-[45vh] overflow-y-auto rounded-lg border">
              {renumberItems.map((item, index) => {
                const previewConfig = numberingConfigFromStyle({
                  format: renumberFormat,
                  padding: renumberPadding,
                  casing: renumberCasing,
                  separator: activeNumberingStyle.separator
                });
                const preview = generateNumberedDisplay(
                  previewConfig,
                  index,
                  parentDisplayNumber
                );
                return (
                  <div
                    key={item.key}
                    draggable={!renumberMutation.isPending}
                    onDragStart={(e) => {
                      setRenumberDragKey(item.key);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      if (!renumberDragKey || renumberDragKey === item.key)
                        return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (renumberDragKey)
                        moveRenumberItem(renumberDragKey, item.key);
                      setRenumberDragKey(null);
                    }}
                    onDragEnd={() => setRenumberDragKey(null)}
                    className={cn(
                      'flex items-center gap-3 border-b px-3 py-2 last:border-b-0',
                      renumberDragKey === item.key && 'bg-muted'
                    )}
                  >
                    <GripVertical
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span
                      className="w-32 shrink-0 truncate whitespace-nowrap font-mono text-sm text-muted-foreground"
                      title={preview}
                    >
                      {preview}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {item.label}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {item.detail}
                      </p>
                    </div>
                    <span className="rounded bg-muted px-2 py-0.5 text-xs capitalize text-muted-foreground">
                      {item.type}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={renumberMutation.isPending}
                onClick={() => setRenumberOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  renumberItems.length === 0 || renumberMutation.isPending
                }
              >
                {renumberMutation.isPending ? 'Applying…' : 'Apply numbering'}
              </Button>
            </div>
          </form>
        </DialogPanel>
      </Dialog>

      <CreateGroupModal
        open={createGroupOpen}
        onOpenChange={setCreateGroupOpen}
        roomId={roomId}
        roomLabel={room?.name}
      />

      <InviteRoomViewerModal
        open={inviteRoomViewerOpen}
        onOpenChange={setInviteRoomViewerOpen}
        roomId={roomId}
        roomName={room?.name}
      />

      <ShareAccessDialog
        open={!!sharePermissionsTarget}
        onOpenChange={(open) => {
          if (!open) setSharePermissionsTarget(null);
        }}
        roomId={roomId}
        target={sharePermissionsTarget}
      />

      <Dialog
        open={moveDialogOpen}
        onOpenChange={(open) => {
          setMoveDialogOpen(open);
          if (!open) setMovePath([]);
        }}
      >
        <DialogPanel className="max-w-lg" aria-labelledby="move-title">
          <h2
            id="move-title"
            className="text-lg font-semibold leading-none tracking-tight"
          >
            Move items
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a destination folder for the selected items.
          </p>
          {moveBlocksRootDestination && !canConfirmMove ? (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-500">
              Move here is disabled at the room root when moving files. Open a
              folder to place them.
            </p>
          ) : null}
          <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-lg border">
            <FolderTreePicker
              folders={folders}
              explorerSelection={explorerSelection}
              path={movePath}
              onPathChange={setMovePath}
              roomName={room?.name ?? 'Room'}
              separator={activeNumberingSeparator}
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={moveMutation.isPending}
              onClick={() => {
                setMoveDialogOpen(false);
                setMovePath([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={moveMutation.isPending || !canConfirmMove}
              title={
                !canConfirmMove
                  ? 'Files cannot be moved to the room root'
                  : undefined
              }
              onClick={() => {
                const itemIds = [...explorerSelection]
                  .map((k) => {
                    if (k.startsWith('file:')) return k.slice(5);
                    if (k.startsWith('folder:')) return k.slice(7);
                    return null;
                  })
                  .filter((id): id is string => !!id);
                if (itemIds.length === 0 || !canConfirmMove) return;
                const destinationFolderId =
                  movePath.length > 0 ? movePath[movePath.length - 1] : null;
                moveMutation.mutate(
                  { itemIds, destinationFolderId },
                  {
                    onSuccess: () => {
                      setMoveDialogOpen(false);
                      setMovePath([]);
                    },
                    onError: (err) => {
                      window.alert(
                        err instanceof Error ? err.message : 'Move failed'
                      );
                    }
                  }
                );
              }}
            >
              {moveMutation.isPending ? 'Moving…' : 'Move here'}
            </Button>
          </div>
        </DialogPanel>
      </Dialog>

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => {
          setRenameOpen(open);
          if (!open) {
            setRenameTarget(null);
            setRenameValue('');
          }
        }}
      >
        <DialogPanel aria-labelledby="rename-title">
          <h2
            id="rename-title"
            className="text-lg font-semibold leading-none tracking-tight"
          >
            Rename
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter a new name.
          </p>
          <form
            className="mt-4 space-y-4"
            onSubmit={async (ev) => {
              ev.preventDefault();
              if (!renameTarget) return;
              const nextName = renameValue.trim();
              if (!nextName || renameBusy) return;
              try {
                if (renameTarget.type === 'folder') {
                  await renameFolderMutation.mutateAsync({
                    id: renameTarget.id,
                    name: nextName
                  });
                } else {
                  await renameFileMutation.mutateAsync({
                    id: renameTarget.id,
                    name: nextName
                  });
                }
                setRenameOpen(false);
                setRenameTarget(null);
                setRenameValue('');
                closeContextMenu();
              } catch (e) {
                console.error(e);
                window.alert(e instanceof Error ? e.message : 'Rename failed');
              }
            }}
          >
            <div className="space-y-2">
              <label htmlFor="rename-input" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="rename-input"
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                autoComplete="off"
                data-1p-ignore
                data-form-type="other"
                autoFocus
                disabled={renameBusy}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={renameBusy}
                onClick={() => {
                  setRenameOpen(false);
                  setRenameTarget(null);
                  setRenameValue('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!renameValue.trim() || renameBusy}
              >
                {renameBusy ? 'Renaming…' : 'Rename'}
              </Button>
            </div>
          </form>
        </DialogPanel>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setDeleteConfirmTargets(null);
        }}
      >
        <DialogPanel aria-labelledby="delete-confirm-title">
          <h2
            id="delete-confirm-title"
            className="flex items-center gap-2 text-lg font-semibold leading-none tracking-tight text-destructive"
          >
            <Trash2 className="h-5 w-5" aria-hidden />
            Delete{' '}
            {deleteConfirmTargets
              ? deleteConfirmTargets.fileIds.length +
                deleteConfirmTargets.folderIds.length
              : ''}{' '}
            item
            {(deleteConfirmTargets?.fileIds.length ?? 0) +
              (deleteConfirmTargets?.folderIds.length ?? 0) ===
            1
              ? ''
              : 's'}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Files are moved to trash; folders and their contents are removed
            permanently.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={deleteBusy}
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteConfirmTargets(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteBusy}
              onClick={() => void executeDeleteConfirmed()}
            >
              {deleteBusy ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogPanel>
      </Dialog>

      {contextMenu.open && contextMenu.target && (
        <div
          data-explorer-context-menu
          role="menu"
          aria-label="Item actions"
          className="fixed z-50 w-56 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{
            left: Math.min(
              contextMenu.x,
              window.innerWidth - EXPLORER_CONTEXT_MENU_WIDTH
            ),
            top: Math.min(contextMenu.y, window.innerHeight - 280)
          }}
        >
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {selectionStats.count > 1
              ? `${selectionStats.count} items selected`
              : contextMenu.target.label}
          </div>
          <div className="h-px bg-border" />
          {contextMenu.target.type === 'folder' ? (
            <>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                disabled={
                  selectionStats.count !== 1 ||
                  selectionStats.folderIds.length !== 1
                }
                onClick={() => {
                  setBrowseFolderIdAndUrl(contextMenu.target!.id);
                  setSelectedFileId(null);
                  closeContextMenu();
                }}
              >
                <FolderOpen className="h-4 w-4" aria-hidden />
                Open
              </button>
              {shouldUseBulkArchiveDownload(selectionInfo) ? (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                  disabled={archiveBusy}
                  onClick={() => void handleBulkDownloadSelected()}
                >
                  <Download className="h-4 w-4" aria-hidden />
                  {bulkDownloadLabel}
                </button>
              ) : null}
              {isAdmin && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                    disabled={!canRename}
                    onClick={() => {
                      const id =
                        selectionStats.folderIds[0] ?? contextMenu.target!.id;
                      const folder = folderById.get(id);
                      if (!folder) return;
                      openRenameDialog({
                        type: 'folder',
                        id,
                        label: folder.name
                      });
                      closeContextMenu();
                    }}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                    Rename
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                    disabled={
                      selectionStats.count !== 1 ||
                      selectionStats.folderIds.length !== 1
                    }
                    onClick={() => {
                      const id =
                        selectionStats.folderIds[0] ?? contextMenu.target!.id;
                      const folder = folderById.get(id);
                      if (!folder) return;
                      openSharePermissionsDialog({
                        type: 'folder',
                        id: folder.id,
                        label: itemLabelWithDisplayNumber(
                          folder.displayNumber,
                          folder.name,
                          activeNumberingSeparator
                        )
                      });
                      closeContextMenu();
                    }}
                  >
                    <Users className="h-4 w-4" aria-hidden />
                    Who can view
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                disabled={
                  useBulkArchiveDownload
                    ? archiveBusy
                    : selectionStats.count !== 1 ||
                      selectionStats.fileIds.length !== 1
                }
                onClick={() => {
                  closeContextMenu();
                  handleDownloadSelected();
                }}
              >
                <Download className="h-4 w-4" aria-hidden />
                {bulkDownloadLabel}
              </button>
              {(() => {
                const id = selectionStats.fileIds[0] ?? contextMenu.target!.id;
                const file = files.find((f) => f.id === id);
                const previewable = isPreviewableMimeType(file?.mimeType);
                if (!previewable) return null;
                return (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                    disabled={
                      selectionStats.count !== 1 ||
                      selectionStats.fileIds.length !== 1
                    }
                    onClick={() => {
                      closeContextMenu();
                      openFilePreview(file!);
                    }}
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                    Preview
                  </button>
                );
              })()}
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                disabled={
                  selectionStats.count !== 1 ||
                  selectionStats.fileIds.length !== 1
                }
                onClick={() => {
                  setDetailsOpen(true);
                  closeContextMenu();
                }}
              >
                <Info className="h-4 w-4" aria-hidden />
                Show details
              </button>
              {isAdmin && (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                  disabled={!canRename}
                  onClick={() => {
                    const id =
                      selectionStats.fileIds[0] ?? contextMenu.target!.id;
                    openRenameDialog({
                      type: 'file',
                      id,
                      label: contextMenu.target!.label
                    });
                    closeContextMenu();
                  }}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                  Rename
                </button>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <div className="my-1 h-px bg-border" />
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                disabled={selectionStats.count === 0 || moveMutation.isPending}
                onClick={() => {
                  closeContextMenu();
                  openMoveDialog();
                }}
              >
                <Move className="h-4 w-4" aria-hidden />
                Move
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-destructive hover:bg-destructive/10"
                disabled={deleteBusy}
                onClick={() => {
                  const target = contextMenu.target;
                  closeContextMenu();
                  openDeleteConfirm(
                    selectionStats.count > 0 || !target
                      ? undefined
                      : target.type === 'file'
                        ? { fileIds: [target.id], folderIds: [] }
                        : { fileIds: [], folderIds: [target.id] }
                  );
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

function FolderTreePicker({
  folders,
  explorerSelection,
  path,
  onPathChange,
  roomName,
  separator
}: {
  folders: FolderRow[];
  explorerSelection: Set<string>;
  path: string[];
  onPathChange: (path: string[]) => void;
  roomName: string;
  separator: string;
}) {
  const selectedFolderIds = React.useMemo(
    () =>
      [...explorerSelection]
        .filter((k) => k.startsWith('folder:'))
        .map((k) => k.slice(7)),
    [explorerSelection]
  );

  const excludedIds = React.useMemo(
    () => excludedFolderIdsForMove(folders, selectedFolderIds),
    [selectedFolderIds, folders]
  );

  const folderById = React.useMemo(() => {
    const map = new Map<string, FolderRow>();
    for (const f of folders) map.set(f.id, f);
    return map;
  }, [folders]);

  const childrenByParent = React.useMemo(() => {
    const map = new Map<string | null, FolderRow[]>();
    for (const f of folders) {
      if (excludedIds.has(f.id)) continue;
      const list = map.get(f.parentId) ?? [];
      list.push(f);
      map.set(f.parentId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return map;
  }, [folders, excludedIds]);

  const currentParentId = path.length > 0 ? path[path.length - 1] : null;
  const currentFolders = React.useMemo(() => {
    const children = childrenByParent.get(currentParentId) ?? [];
    return children.filter((f) => !excludedIds.has(f.id));
  }, [childrenByParent, currentParentId, excludedIds]);

  const breadcrumb = React.useMemo(() => {
    const result: { id: string | null; name: string; isRoom: boolean }[] = [
      { id: null, name: roomName, isRoom: true }
    ];
    for (const id of path) {
      const folder = folderById.get(id);
      if (folder)
        result.push({
          id,
          name: itemLabelWithDisplayNumber(
            folder.displayNumber,
            folder.name,
            separator
          ),
          isRoom: false
        });
    }
    return result;
  }, [path, folderById, roomName, separator]);

  return (
    <div className="flex flex-col">
      {/* Breadcrumb path */}
      <div className="flex flex-wrap items-center gap-1 border-b px-3 py-2 text-sm">
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item.id ?? 'root'}>
            {index > 0 && (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
            <button
              type="button"
              onClick={() => onPathChange(path.slice(0, index))}
              className={cn(
                'flex items-center gap-1 hover:text-foreground',
                index === breadcrumb.length - 1
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground'
              )}
            >
              {item.isRoom && <DoorOpen className="h-3.5 w-3.5" aria-hidden />}
              {item.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Folder list */}
      <div>
        {currentFolders.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            No folders here
          </div>
        ) : (
          currentFolders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => onPathChange([...path, folder.id])}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent/50"
            >
              <FolderOpen
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <span className="flex-1 truncate text-left">
                {itemLabelWithDisplayNumber(
                  folder.displayNumber,
                  folder.name,
                  separator
                )}
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function RoomExplorerRoute() {
  const { roomId } = Route.useParams();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const atExplorer =
    pathname === `/rooms/${roomId}` || pathname === `/rooms/${roomId}/`;

  return atExplorer ? <RoomExplorerComponent roomId={roomId} /> : <Outlet />;
}
